import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  Modal,
  Box,
  SpaceBetween,
  Header,
  Button,
  Form,
  FormField,
  Input,
  Toggle,
  Select,
  Container,
  ColumnLayout,
  Alert,
  DateRangePicker,
  TimeInput,
  Grid,
  Multiselect,
  ExpandableSection,
  Tabs,
  Badge,
  Spinner,
  Textarea
} from '@cloudscape-design/components';
import { 
  personalPromotionsFormModalStore,
} from '../stores/personalPromotions';
import {
  RISK_MODEL_OPTIONS,
  getRiskModelSelectedOptions,
  normalizeRiskModelValues
} from '../helpers/riskModelOptions';

function PersonalPromotionsFormModal(props) {
  const store = useRecoilValue(personalPromotionsFormModalStore);
  const setStore = useSetRecoilState(personalPromotionsFormModalStore);

  const [activeTab, setActiveTab] = React.useState('basic');
  const [validationErrors, setValidationErrors] = React.useState({});
  const [imagePreview, setImagePreview] = React.useState(null);
  const [cdnUrl, setCdnUrl] = React.useState('');
  const [isCdnLoading, setIsCdnLoading] = React.useState(false);
  const [imageLoadError, setImageLoadError] = React.useState({ current: false, duplicated: false });
  const [imageLoading, setImageLoading] = React.useState({ current: false, duplicated: false });
  
  const LIMITS = {
    bonusPercent: { min: 1, max: 300 },
    ttlMinutes: { min: 1, max: 1440 },
    depositAmount: { min: 0, max: 1000000000 },
    betsMultiplier: { min: 1, max: 1000000 },
    accountAge: { min: 0, max: 8000 },
    depositsCount: { min: 0, max: 999999 },
    betsCount: { min: 0, max: 100000 },
    periodDays: { min: 1, max: 365 },
    maxTriggers: { min: 1, max: 10000 },
    windowSeconds: { min: 1, max: 2592000 },
    depositsAmount: { min: 0, max: 1000000000 }
  };

  const DEFAULT_DEPOSIT_THRESHOLD = {
    minDepositAmount: 50000,
    maxDepositAmount: 10000000,
    bonusPercentAmount: 50
  };

  const getLegacyDepositThreshold = (data = {}) => ({
    minDepositAmount: data.minDepositAmount !== undefined ? data.minDepositAmount : DEFAULT_DEPOSIT_THRESHOLD.minDepositAmount,
    maxDepositAmount: data.maxDepositAmount !== undefined ? data.maxDepositAmount : DEFAULT_DEPOSIT_THRESHOLD.maxDepositAmount,
    bonusPercentAmount: data.bonusPercentAmount !== undefined ? data.bonusPercentAmount : DEFAULT_DEPOSIT_THRESHOLD.bonusPercentAmount
  });

  const normalizeDepositThresholds = (data = {}) => {
    const thresholds = Array.isArray(data.depositThresholds)
      ? data.depositThresholds
      : [getLegacyDepositThreshold(data)];

    return thresholds.map(threshold => ({
      minDepositAmount: Number(threshold.minDepositAmount) || 0,
      maxDepositAmount: Number(threshold.maxDepositAmount) || 0,
      bonusPercentAmount: Number(threshold.bonusPercentAmount) || 0
    }));
  };

  const getDataWithDepositThresholds = (data = {}) => {
    const depositThresholds = normalizeDepositThresholds(data);
    const depositData = { ...data };
    delete depositData.minDepositAmount;
    delete depositData.maxDepositAmount;
    delete depositData.bonusPercentAmount;

    return {
      ...depositData,
      depositThresholds: depositThresholds
    };
  };

  const getBetCountConditions = (bet = {}) => {
    if (!Array.isArray(bet.all) || bet.all.length === 0) {
      return {};
    }

    return {
      all: bet.all.map(condition => ({
        minBetsCount: condition.minBetsCount,
        maxBetsCount: condition.maxBetsCount
      }))
    };
  };

  const getMaxBonusAmount = (threshold) => {
    const maxDepositAmount = Number(threshold.maxDepositAmount) || 0;
    const bonusPercentAmount = Number(threshold.bonusPercentAmount) || 0;
    return Math.floor(maxDepositAmount * bonusPercentAmount / 100);
  };
  
  const isDarkMode = () => {
    return localStorage.getItem("mode") === "dark";
  };

  const formatCurrency = (kopeks) => {
    const rubles = kopeks / 100;
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(rubles) + ' ₽';
  };
  
  const parseUtcTimeToMinutes = (value) => {
    if (!value || typeof value !== 'string') {
      return null;
    }
    
    const parts = value.split(':');
    if (parts.length < 2) {
      return null;
    }
    
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
      return null;
    }
    
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    
    return hours * 60 + minutes;
  };

  const getMainUtcDateString = () => {
    if (!store.formData.startDate || !store.formData.endDate) {
      return '';
    }
    
    const [datePart] = store.formData.startDate.split('T');
    return datePart || '';
  };
  
  const getDailyUtcWindows = () => {
    const windows = store.formData.triggerConditions?.dailyUtcWindows;
    return Array.isArray(windows) ? windows : [];
  };

  const getDailyUtcWindowsError = (windows, options = {}) => {
    const {
      requireComplete = true,
      requireMainDate = false
    } = options;
    
    if (requireMainDate && (!store.formData.startDate || !store.formData.endDate)) {
      return null;
    }
    
    if (!windows || windows.length === 0) {
      return null;
    }
    
    if (windows.length > 3) {
      return 'No more than 3 daily UTC windows are allowed';
    }
    
    const parsedWindows = [];
    
    for (let i = 0; i < windows.length; i++) {
      const window = windows[i] || {};
      const hasStart = window.start !== undefined && window.start !== '';
      const hasEnd = window.end !== undefined && window.end !== '';
      
      if (!requireComplete && (!hasStart || !hasEnd)) {
        continue;
      }
      
      const startMinutes = parseUtcTimeToMinutes(window.start);
      const endMinutes = parseUtcTimeToMinutes(window.end);
      
      if (startMinutes === null || endMinutes === null) {
        return 'Each window must have a valid start and end time (HH:mm).';
      }
      
      if (startMinutes >= endMinutes) {
        return `Window #${i + 1} start time must be before end time.`;
      }
      
      parsedWindows.push({ start: startMinutes, end: endMinutes });
    }
    
    if (parsedWindows.length > 1) {
      const sortedWindows = [...parsedWindows].sort((a, b) => a.start - b.start);
      for (let i = 1; i < sortedWindows.length; i++) {
        if (sortedWindows[i].start < sortedWindows[i - 1].end) {
          return 'Daily UTC windows must not overlap.';
        }
      }
    }
    
    return null;
  };
  
  
  const getImageUrl = (imageName) => {
    if (!imageName || !cdnUrl) {
      return '';
    }
    const imageFileName = imageName.includes('.jpg') ? imageName : `${imageName}.jpg`;
    const fullUrl = `${cdnUrl}/assets/jpg/PersonalizedPromotions/${imageFileName}`;
    return fullUrl;
  };
  
  React.useEffect(() => {
    if (store.isOpen && props.Interface) {
      setActiveTab('basic');
      setIsCdnLoading(true);
      setImageLoadError({ current: false, duplicated: false });
      setImageLoading({ current: false, duplicated: false });
      
      props.Interface.request("get", { 
        class: "data", 
        module: "personalPromotions", 
        method: "getConfig"
      }, {})
      .then(response => {
        if (response?.cdn) {
          setCdnUrl(response.cdn);
        }
        setIsCdnLoading(false);
      })
      .catch(() => {
        setIsCdnLoading(false);
      });
    }
  }, [store.isOpen]);

  React.useEffect(() => {
    if (cdnUrl && store.formData.image && store.isOpen) {
      if (store.mode === 'edit') {
        setImageLoading(prev => ({ ...prev, current: true }));
      } else if (store.mode === 'add' && store.formData.image && !store.editingId) {
        setImageLoading(prev => ({ ...prev, duplicated: true }));
      }
    }
  }, [cdnUrl, store.formData.image, store.mode, store.isOpen]);

  const handleClose = () => {
    setActiveTab('basic');
    setImagePreview(null);
    setCdnUrl('');
    setImageLoadError({ current: false, duplicated: false });
    setImageLoading({ current: false, duplicated: false });
    setStore(prev => ({
      ...prev,
      isOpen: false,
      formData: {
        internal_name: '',
        internal_description: '',
        type: 'money',
        status: 'active',
        priority: 0,
        image: '',
        imageFile: null,
        startDate: '',
        endDate: '',
        data: {
          depositThresholds: [
            {
              minDepositAmount: 50000,
              maxDepositAmount: 10000000,
              bonusPercentAmount: 50
            }
          ],
          bonusType: 'all',
          ttlMinutes: 10
        },
        triggerConditions: {
          frequency: [
            {
              scope: 'per_user',
              rules: [
                {
                  type: 'lifetime',
                  maxTriggers: 15,
                  countsOn: 'use'
                },
                {
                  type: 'rolling_window',
                  windowSeconds: 18,
                  maxTriggers: 2,
                  countsOn: 'trigger'
                },
                {
                  type: 'per_day',
                  maxTriggers: 10,
                  countsOn: 'use'
                }
              ]
            },
            {
              scope: 'global',
              rules: [
                {
                  type: 'lifetime',
                  maxTriggers: 10,
                  countsOn: 'use'
                },
                {
                  type: 'rolling_window',
                  windowSeconds: 18,
                  maxTriggers: 2,
                  countsOn: 'trigger'
                },
                {
                  type: 'per_day',
                  maxTriggers: 10,
                  countsOn: 'use'
                }
              ]
            }
          ],
          balanceGuard: {
            minCurrentBetAmount: 10000,
            remainingBetsMultiplier: 5,
            checkAfterPlacingCurrentBet: true,
            comparison: '<='
          },
          dailyUtcWindows: [],
          deposit: {},
          account: {
            requiredSocialAccounts: []
          },
          bet: {}
        }
      },
      error: null
    }));
  };

  const validateField = (fieldName, value, limits) => {
    const errors = [];
    const numValue = Number(value);
    
    if (isNaN(numValue) && limits) {
      errors.push('Must be a valid number');
      return errors;
    }
    
    if (limits) {
      if (limits.min !== undefined && numValue < limits.min) {
        errors.push(`Minimum value is ${limits.min}`);
      }
      if (limits.max !== undefined && numValue > limits.max) {
        errors.push(`Maximum value is ${limits.max.toLocaleString()}`);
      }
    }
    
    return errors;
  };
  
  const validateForm = () => {
    const errors = {};
    const internalName = store.formData.internal_name || '';
    const internalDescription = store.formData.internal_description || '';

    if (!internalName.trim()) {
      errors.internal_name = ['Internal name is required'];
    } else if (internalName.length > 30) {
      errors.internal_name = ['Internal name must be up to 30 characters'];
    }

    if (internalDescription.length > 120) {
      errors.internal_description = ['Internal description must be up to 120 characters'];
    }
    
    if (!store.formData.startDate || !store.formData.endDate) {
      errors.dateRange = ['Both start and end dates with time are required'];
    } else {
      const start = new Date(store.formData.startDate);
      const end = new Date(store.formData.endDate);
      const now = new Date();
      if (start >= end) {
        errors.dateRange = ['End date must be after start date'];
      } else if (store.mode === 'add' && end < now) {
        errors.dateRange = ['End date must be in the future for new promotions'];
      } else if (store.mode === 'edit' && end < now) {
        errors.dateRange = ['Warning: This promotion end date is in the past'];
      }
    }

    const dailyUtcWindows = getDailyUtcWindows();
    const dailyUtcWindowsError = getDailyUtcWindowsError(dailyUtcWindows);
    if (dailyUtcWindowsError) {
      errors.dailyUtcWindows = [dailyUtcWindowsError];
    }
    
    if (!store.formData.image && !store.formData.imageFile) {
      errors.image = ['Image is required (540x300 px)'];
    }
    
    const frequency = store.formData.triggerConditions.frequency;
    if (!frequency || frequency.length === 0) {
      errors.frequency = ['At least one frequency rule is required'];
    } else {
      for (let i = 0; i < frequency.length; i++) {
        const scope = frequency[i];
        if (!scope.rules || scope.rules.length === 0) {
          errors.frequency = [`Frequency scope ${scope.scope} must have at least one rule`];
          break;
        }
        for (let j = 0; j < scope.rules.length; j++) {
          const rule = scope.rules[j];
          if (!rule.maxTriggers || rule.maxTriggers < LIMITS.maxTriggers.min) {
            if (!errors.frequencyRules) errors.frequencyRules = {};
            errors.frequencyRules[`${i}-${j}-maxTriggers`] = [`Max Triggers must be at least ${LIMITS.maxTriggers.min}`];
          }
          if (rule.type === 'rolling_window' && (!rule.windowSeconds || rule.windowSeconds < LIMITS.windowSeconds.min)) {
            if (!errors.frequencyRules) errors.frequencyRules = {};
            errors.frequencyRules[`${i}-${j}-windowSeconds`] = [`Time Window must be at least ${LIMITS.windowSeconds.min}`];
          }
        }
      }
    }
    
    const data = getDataWithDepositThresholds(store.formData.data);
    const depositThresholds = normalizeDepositThresholds(data);

    if (depositThresholds.length < 1 || depositThresholds.length > 3) {
      if (!errors.data) errors.data = {};
      errors.data.depositThresholds = ['Deposit thresholds count must be from 1 to 3'];
    }

    depositThresholds.forEach((threshold, index) => {
      const thresholdNumber = index + 1;
      const minErrors = validateField('minDepositAmount', threshold.minDepositAmount, LIMITS.depositAmount);
      const maxErrors = validateField('maxDepositAmount', threshold.maxDepositAmount, LIMITS.depositAmount);
      const bonusErrors = validateField('bonusPercentAmount', threshold.bonusPercentAmount, LIMITS.bonusPercent);

      if (minErrors.length > 0) {
        if (!errors.data) errors.data = {};
        errors.data[`depositThresholds-${index}-minDepositAmount`] = minErrors;
      }

      if (maxErrors.length > 0) {
        if (!errors.data) errors.data = {};
        errors.data[`depositThresholds-${index}-maxDepositAmount`] = maxErrors;
      }

      if (bonusErrors.length > 0) {
        if (!errors.data) errors.data = {};
        errors.data[`depositThresholds-${index}-bonusPercentAmount`] = bonusErrors;
      }

      if (threshold.minDepositAmount >= threshold.maxDepositAmount) {
        if (!errors.data) errors.data = {};
        errors.data[`depositThresholds-${index}-minDepositAmount`] = [`Threshold #${thresholdNumber}: must be less than max deposit`];
        errors.data[`depositThresholds-${index}-maxDepositAmount`] = [`Threshold #${thresholdNumber}: must be greater than min deposit`];
      }

      if (index > 0 && threshold.minDepositAmount <= depositThresholds[index - 1].minDepositAmount) {
        if (!errors.data) errors.data = {};
        errors.data[`depositThresholds-${index}-minDepositAmount`] = [`Threshold #${thresholdNumber}: must be greater than previous threshold`];
      }

      if (index > 0 && depositThresholds[index - 1].maxDepositAmount > threshold.minDepositAmount) {
        if (!errors.data) errors.data = {};
        errors.data[`depositThresholds-${index - 1}-maxDepositAmount`] = [`Threshold #${index}: max deposit cannot exceed next threshold min deposit`];
        errors.data[`depositThresholds-${index}-minDepositAmount`] = [`Threshold #${thresholdNumber}: min deposit must be at least previous max deposit`];
      }
    });

    if (errors.data) {
      const firstThresholdError = Object.keys(errors.data).find(key => key.indexOf('depositThresholds-') === 0);
      if (firstThresholdError) {
        errors.data.depositThresholds = [errors.data[firstThresholdError][0]];
      }
    }
    
    const ttlErrors = validateField('ttlMinutes', data.ttlMinutes, LIMITS.ttlMinutes);
    if (ttlErrors.length > 0) {
      if (!errors.data) errors.data = {};
      errors.data.ttlMinutes = ttlErrors;
    }
    
    const bg = store.formData.triggerConditions.balanceGuard;
    if (bg.remainingBetsMultiplier !== undefined) {
      const multiplierErrors = validateField('remainingBetsMultiplier', bg.remainingBetsMultiplier, LIMITS.betsMultiplier);
      if (multiplierErrors.length > 0) {
        if (!errors.balanceGuard) errors.balanceGuard = {};
        errors.balanceGuard.remainingBetsMultiplier = multiplierErrors;
      }
    }
    
    const account = store.formData.triggerConditions.account;
    
    if (account.minAccountAgeDays !== undefined && account.maxAccountAgeDays !== undefined) {
      if (account.minAccountAgeDays > account.maxAccountAgeDays) {
        if (!errors.account) errors.account = {};
        errors.account.minAccountAgeDays = ['Minimum age cannot be greater than maximum age'];
        errors.account.maxAccountAgeDays = ['Maximum age cannot be less than minimum age'];
      }
    }
    
    const deposit = store.formData.triggerConditions.deposit;
    const hasDepositConditions = 
      (deposit.all && deposit.all.length > 0) ||
      (deposit.fiat && deposit.fiat.length > 0) ||
      (deposit.crypto && deposit.crypto.length > 0);
    const bet = store.formData.triggerConditions.bet;
    const hasBetConditions = Array.isArray(bet?.all) && bet.all.length > 0;
    
    if (!hasDepositConditions) {
      errors.depositConditions = [
        hasBetConditions
          ? 'Bet count range requires at least one Deposit Condition'
          : 'At least one deposit condition is required for the promotion to work'
      ];
    }
    
    ['all', 'fiat', 'crypto'].forEach(type => {
      if (deposit[type] && deposit[type].length > 0) {
        deposit[type].forEach((condition, index) => {
          if (condition.minDepositsAmount !== undefined && condition.maxDepositsAmount !== undefined) {
            if (condition.minDepositsAmount > condition.maxDepositsAmount) {
              if (!errors.deposit) errors.deposit = {};
              errors.deposit[`${type}-${index}-minDepositsAmount`] = ['Minimum amount cannot be greater than maximum amount'];
              errors.deposit[`${type}-${index}-maxDepositsAmount`] = ['Maximum amount cannot be less than minimum amount'];
            }
          }
          
          if (condition.minDepositsCount !== undefined && condition.maxDepositsCount !== undefined) {
            if (condition.minDepositsCount > condition.maxDepositsCount) {
              if (!errors.deposit) errors.deposit = {};
              errors.deposit[`${type}-${index}-minDepositsCount`] = ['Minimum count cannot be greater than maximum count'];
              errors.deposit[`${type}-${index}-maxDepositsCount`] = ['Maximum count cannot be less than minimum count'];
            }
          }
          
          // Validate limits
          const minAmountErrors = validateField('minDepositsAmount', condition.minDepositsAmount, LIMITS.depositsAmount);
          if (minAmountErrors.length > 0) {
            if (!errors.deposit) errors.deposit = {};
            errors.deposit[`${type}-${index}-minDepositsAmount`] = minAmountErrors;
          }
          
          const maxAmountErrors = validateField('maxDepositsAmount', condition.maxDepositsAmount, LIMITS.depositsAmount);
          if (maxAmountErrors.length > 0) {
            if (!errors.deposit) errors.deposit = {};
            errors.deposit[`${type}-${index}-maxDepositsAmount`] = maxAmountErrors;
          }
          
          const minCountErrors = validateField('minDepositsCount', condition.minDepositsCount, LIMITS.depositsCount);
          if (minCountErrors.length > 0) {
            if (!errors.deposit) errors.deposit = {};
            errors.deposit[`${type}-${index}-minDepositsCount`] = minCountErrors;
          }
          
          const maxCountErrors = validateField('maxDepositsCount', condition.maxDepositsCount, LIMITS.depositsCount);
          if (maxCountErrors.length > 0) {
            if (!errors.deposit) errors.deposit = {};
            errors.deposit[`${type}-${index}-maxDepositsCount`] = maxCountErrors;
          }
          
          if (condition.periodDays !== undefined) {
            const periodErrors = validateField('periodDays', condition.periodDays, LIMITS.periodDays);
            if (periodErrors.length > 0) {
              if (!errors.deposit) errors.deposit = {};
              errors.deposit[`${type}-${index}-periodDays`] = periodErrors;
            }
          }
        });
      }
    });
    
    if (bet && bet.all) {
      bet.all.forEach((condition, index) => {
        const minBetsCountMissing =
          condition.minBetsCount === undefined ||
          condition.minBetsCount === null ||
          condition.minBetsCount === '';
        const maxBetsCountMissing =
          condition.maxBetsCount === undefined ||
          condition.maxBetsCount === null ||
          condition.maxBetsCount === '';

        if (minBetsCountMissing) {
          if (!errors.bet) errors.bet = {};
          errors.bet[`${index}-minBetsCount`] = ['Minimum count is required'];
        }

        if (maxBetsCountMissing) {
          if (!errors.bet) errors.bet = {};
          errors.bet[`${index}-maxBetsCount`] = ['Maximum count is required'];
        }

        if (!minBetsCountMissing && !maxBetsCountMissing) {
          if (condition.minBetsCount > condition.maxBetsCount) {
            if (!errors.bet) errors.bet = {};
            errors.bet[`${index}-minBetsCount`] = ['Minimum count cannot be greater than maximum count'];
            errors.bet[`${index}-maxBetsCount`] = ['Maximum count cannot be less than minimum count'];
          }
        }

        const minCountErrors = minBetsCountMissing
          ? []
          : validateField('minBetsCount', condition.minBetsCount, LIMITS.betsCount);
        if (minCountErrors.length > 0 && !errors.bet?.[`${index}-minBetsCount`]) {
          if (!errors.bet) errors.bet = {};
          errors.bet[`${index}-minBetsCount`] = minCountErrors;
        }

        const maxCountErrors = maxBetsCountMissing
          ? []
          : validateField('maxBetsCount', condition.maxBetsCount, LIMITS.betsCount);
        if (maxCountErrors.length > 0 && !errors.bet?.[`${index}-maxBetsCount`]) {
          if (!errors.bet) errors.bet = {};
          errors.bet[`${index}-maxBetsCount`] = maxCountErrors;
        }
      });
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      if (errors.frequencyRules) {
        if (!errors.frequency) errors.frequency = {};
        Object.keys(errors.frequencyRules).forEach(key => {
          errors.frequency[key] = errors.frequencyRules[key];
        });
      }
      
      setValidationErrors(errors);
      
      let errorMessage = 'Please fix the following errors:\n';
      if (errors.internal_name) errorMessage += `• Internal name: ${errors.internal_name[0]}\n`;
      if (errors.internal_description) errorMessage += `• Internal description: ${errors.internal_description[0]}\n`;
      if (errors.dateRange) errorMessage += `• ${errors.dateRange[0]}\n`;
      if (errors.dailyUtcWindows) errorMessage += `• Daily UTC Windows: ${errors.dailyUtcWindows[0]}\n`;
      if (errors.image) errorMessage += `• ${errors.image[0]}\n`;
      if (errors.frequency && Array.isArray(errors.frequency)) errorMessage += `• Frequency: ${errors.frequency[0]}\n`;
      if (errors.depositConditions) errorMessage += `• Deposit Conditions: ${errors.depositConditions[0]}\n`;
      if (errors.data?.depositThresholds) errorMessage += `• Deposit Thresholds: ${errors.data.depositThresholds[0]}\n`;
      if (errors.data?.ttlMinutes) errorMessage += `• TTL: ${errors.data.ttlMinutes[0]}\n`;
      if (errors.balanceGuard?.remainingBetsMultiplier) errorMessage += `• Remaining Bets Multiplier: ${errors.balanceGuard.remainingBetsMultiplier[0]}\n`;
      if (errors.account?.minAccountAgeDays) errorMessage += `• Min Account Age: ${errors.account.minAccountAgeDays[0]}\n`;
      if (errors.account?.maxAccountAgeDays) errorMessage += `• Max Account Age: ${errors.account.maxAccountAgeDays[0]}\n`;
      
      if (errors.deposit && errors.deposit !== errors.depositConditions) {
        const depositErrors = new Set();
        Object.keys(errors.deposit).forEach(key => {
          const parts = key.split('-');
          const type = parts[0];
          const index = parts[1];
          const field = parts.slice(2).join('-');
          const conditionNumber = parseInt(index) + 1;
          const typeLabel = type === 'all' ? 'All' : type === 'fiat' ? 'Fiat' : 'Crypto';
          
          if (field === 'minDepositsAmount' || field === 'maxDepositsAmount') {
            if (errors.deposit[key][0].includes('cannot be greater than') || errors.deposit[key][0].includes('cannot be less than')) {
              depositErrors.add(`• ${typeLabel} Deposit Condition #${conditionNumber}: Min/Max amounts are contradictory\n`);
            } else {
              errorMessage += `• ${typeLabel} Deposit Condition #${conditionNumber} ${field === 'minDepositsAmount' ? 'Min' : 'Max'} Amount: ${errors.deposit[key][0]}\n`;
            }
          } else if (field === 'minDepositsCount' || field === 'maxDepositsCount') {
            if (errors.deposit[key][0].includes('cannot be greater than') || errors.deposit[key][0].includes('cannot be less than')) {
              depositErrors.add(`• ${typeLabel} Deposit Condition #${conditionNumber}: Min/Max counts are contradictory\n`);
            } else {
              errorMessage += `• ${typeLabel} Deposit Condition #${conditionNumber} ${field === 'minDepositsCount' ? 'Min' : 'Max'} Count: ${errors.deposit[key][0]}\n`;
            }
          } else if (field === 'periodDays') {
            errorMessage += `• ${typeLabel} Deposit Condition #${conditionNumber} Period: ${errors.deposit[key][0]}\n`;
          }
        });
        
        depositErrors.forEach(error => {
          errorMessage += error;
        });
      }
      
      if (errors.bet) {
        const betErrors = new Set();
        Object.keys(errors.bet).forEach(key => {
          const [index, field] = key.split('-');
          const conditionNumber = parseInt(index) + 1;
          
          if (field === 'minBetsCount' || field === 'maxBetsCount') {
            if (errors.bet[key][0].includes('cannot be greater than') || errors.bet[key][0].includes('cannot be less than')) {
              betErrors.add(`• Bet Condition #${conditionNumber}: Min/Max counts are contradictory\n`);
            } else {
              errorMessage += `• Bet Condition #${conditionNumber} ${field === 'minBetsCount' ? 'Min' : 'Max'} Count: ${errors.bet[key][0]}\n`;
            }
          }
        });
        
        betErrors.forEach(error => {
          errorMessage += error;
        });
      }
      
      if (errors.frequencyRules) {
        Object.keys(errors.frequencyRules).forEach(key => {
          const [scopeIndex, ruleIndex, field] = key.split('-');
          if (field === 'maxTriggers') {
            errorMessage += `• Frequency Rule #${parseInt(ruleIndex) + 1}: ${errors.frequencyRules[key][0]}\n`;
          } else if (field === 'windowSeconds') {
            errorMessage += `• Frequency Rule #${parseInt(ruleIndex) + 1} Time Window: ${errors.frequencyRules[key][0]}\n`;
          }
        });
      }
      
      setStore(prev => ({ ...prev, error: errorMessage }));
      return;
    }
    
    setValidationErrors({});
    setStore(prev => ({ ...prev, loading: true, error: null }));
    
    try {

      let imageBase64 = null;
      if (store.formData.imageFile) {
        imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(store.formData.imageFile);
        });
      }

      const triggerConditions = {
        ...store.formData.triggerConditions,
        dailyUtcWindows: getDailyUtcWindows(),
        bet: getBetCountConditions(store.formData.triggerConditions.bet),
        account: {
          ...store.formData.triggerConditions.account,
          riskModel: normalizeRiskModelValues(store.formData.triggerConditions.account?.riskModel)
        }
      };

      const promotionData = getDataWithDepositThresholds(store.formData.data);
      
      const requestData = {
        internal_name: (store.formData.internal_name || '').trim(),
        internal_description: store.formData.internal_description || '',
        type: store.formData.type,
        data: promotionData,
        triggerConditions: triggerConditions,
        status: store.formData.status,
        priority: Number.isFinite(Number(store.formData.priority))
          ? parseInt(store.formData.priority, 10)
          : 0,
        startDate: store.formData.startDate,
        endDate: store.formData.endDate
      };

      if (imageBase64) {
        requestData.image = imageBase64;
      } else if (store.mode === 'edit' && store.formData.image && !store.formData.imageFile) {
      } else if (store.mode === 'add' && store.formData.image && !store.formData.imageFile) {
        requestData.image = store.formData.image;
      } else if (store.mode === 'add' && !store.formData.image) {
        requestData.image = null;
      }

      if (store.mode === 'edit') {
        requestData.id = store.editingId;
      }

      await props.Interface.request("set", {
        class: "data",
        module: "personalPromotions",
        method: store.mode === 'add' ? 'add' : 'edit'
      }, requestData);

      props.Interface.addNoty(
        "Success", 
        `Personal promotion was successfully ${store.mode === 'add' ? 'added' : 'updated'}`
      );

      if (props.Interface.loadPersonalPromotionsList) {
        props.Interface.loadPersonalPromotionsList();
      }

      setStore(prev => ({ ...prev, loading: false }));
      handleClose();
    } catch (error) {
      console.error('Error submitting promotion:', error);
      
      let errorMessage = 'Failed to save promotion';
      
      if (error?.text) {
        errorMessage = error.text;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setStore(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMessage
      }));
    }
  };

  const updateData = (field, value) => {
    setStore(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        data: {
          ...prev.formData.data,
          [field]: value
        }
      }
    }));
  };

  const updateFormField = (field, value) => {
    setStore(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value
      }
    }));
    setValidationErrors(prev => {
      if (!prev[field]) {
        return prev;
      }

      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const updateDepositThreshold = (index, field, value) => {
    setStore(prev => {
      const data = getDataWithDepositThresholds(prev.formData.data);
      const depositThresholds = data.depositThresholds.map((threshold, thresholdIndex) => (
        thresholdIndex === index ? { ...threshold, [field]: value } : threshold
      ));

      return {
        ...prev,
        formData: {
          ...prev.formData,
          data: {
            ...data,
            depositThresholds: depositThresholds
          }
        }
      };
    });
  };

  const addDepositThreshold = () => {
    setStore(prev => {
      const data = getDataWithDepositThresholds(prev.formData.data);
      const depositThresholds = data.depositThresholds;

      if (depositThresholds.length >= 3) {
        return prev;
      }

      const lastThreshold = depositThresholds[depositThresholds.length - 1] || DEFAULT_DEPOSIT_THRESHOLD;
      const nextMinDepositAmount = Number(lastThreshold.maxDepositAmount) || 0;
      const nextThreshold = {
        minDepositAmount: nextMinDepositAmount,
        maxDepositAmount: nextMinDepositAmount + 10000000,
        bonusPercentAmount: Number(lastThreshold.bonusPercentAmount) || DEFAULT_DEPOSIT_THRESHOLD.bonusPercentAmount
      };

      return {
        ...prev,
        formData: {
          ...prev.formData,
          data: {
            ...data,
            depositThresholds: [...depositThresholds, nextThreshold]
          }
        }
      };
    });
  };

  const removeDepositThreshold = (index) => {
    setStore(prev => {
      const data = getDataWithDepositThresholds(prev.formData.data);

      if (data.depositThresholds.length <= 1) {
        return prev;
      }

      const depositThresholds = data.depositThresholds.filter((_, thresholdIndex) => thresholdIndex !== index);

      return {
        ...prev,
        formData: {
          ...prev.formData,
          data: {
            ...data,
            depositThresholds: depositThresholds
          }
        }
      };
    });
  };

  const updateTriggerConditions = (path, value) => {
    setStore(prev => {
      const newFormData = { 
        ...prev.formData,
        triggerConditions: JSON.parse(JSON.stringify(prev.formData.triggerConditions))
      };
      
      const pathArray = path.split('.');
      let current = newFormData.triggerConditions;
      
      for (let i = 0; i < pathArray.length - 1; i++) {
        if (!current[pathArray[i]]) {
          current[pathArray[i]] = {};
        }
        current = current[pathArray[i]];
      }
      
      current[pathArray[pathArray.length - 1]] = value;
      
      return {
        ...prev,
        formData: newFormData
      };
    });
  };

  const clearDailyUtcWindowsErrors = () => {
    setValidationErrors(prev => {
      if (!prev.dailyUtcWindows) {
        return prev;
      }
      
      const newErrors = { ...prev };
      delete newErrors.dailyUtcWindows;
      return newErrors;
    });
  };

  const addDailyUtcWindow = () => {
    const current = getDailyUtcWindows();
    if (current.length >= 3) {
      return;
    }
    
    const next = [...current, { start: '', end: '' }];
    updateTriggerConditions('dailyUtcWindows', next);
    
    const error = getDailyUtcWindowsError(next, { requireComplete: false, requireMainDate: true });
    if (error) {
      setValidationErrors(prev => ({ ...prev, dailyUtcWindows: [error] }));
    } else {
      clearDailyUtcWindowsErrors();
    }
  };

  const setDailyUtcWindow = (index, values) => {
    const current = getDailyUtcWindows();
    const next = current.map((window, idx) => (
      idx === index ? { ...window, ...values } : window
    ));
    
    updateTriggerConditions('dailyUtcWindows', next);
    
    const error = getDailyUtcWindowsError(next, { requireComplete: false, requireMainDate: true });
    if (error) {
      setValidationErrors(prev => ({ ...prev, dailyUtcWindows: [error] }));
    } else {
      clearDailyUtcWindowsErrors();
    }
  };

  const removeDailyUtcWindow = (index) => {
    const current = getDailyUtcWindows();
    const next = current.filter((_, idx) => idx !== index);
    
    updateTriggerConditions('dailyUtcWindows', next);
    
    const error = getDailyUtcWindowsError(next, { requireComplete: false, requireMainDate: true });
    if (error) {
      setValidationErrors(prev => ({ ...prev, dailyUtcWindows: [error] }));
    } else {
      clearDailyUtcWindowsErrors();
    }
  };

  const updateFrequencyRule = (scopeIndex, ruleIndex, field, value) => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      newTriggerConditions.frequency[scopeIndex].rules[ruleIndex][field] = value;
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
  };

  const addFrequencyRule = (scopeIndex) => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      newTriggerConditions.frequency[scopeIndex].rules.push({
        type: 'lifetime',
        maxTriggers: 1,
        countsOn: 'use'
      });
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.frequency;
      return newErrors;
    });
  };

  const removeFrequencyRule = (scopeIndex, ruleIndex) => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      newTriggerConditions.frequency[scopeIndex].rules.splice(ruleIndex, 1);
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
  };

  const addFrequencyScope = (scopeType) => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      
      const existingScope = newTriggerConditions.frequency.find(f => f.scope === scopeType);
      if (existingScope) return prev;
      
      newTriggerConditions.frequency.push({
        scope: scopeType,
        rules: [
          {
            type: 'per_day',
            maxTriggers: 1,
            countsOn: 'use'
          }
        ]
      });
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.frequency;
      return newErrors;
    });
  };

  const removeFrequencyScope = (scopeIndex) => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      newTriggerConditions.frequency.splice(scopeIndex, 1);
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
  };

  const updateDepositCondition = (type, index, field, value) => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      if (!newTriggerConditions.deposit[type]) newTriggerConditions.deposit[type] = [];
      if (!newTriggerConditions.deposit[type][index]) newTriggerConditions.deposit[type][index] = {};
      newTriggerConditions.deposit[type][index][field] = value;
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
    
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.depositConditions;
      return newErrors;
    });
  };

  const updateBetCondition = (type, index, field, value) => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      if (!newTriggerConditions.bet[type]) newTriggerConditions.bet[type] = [];
      if (!newTriggerConditions.bet[type][index]) newTriggerConditions.bet[type][index] = {};
      newTriggerConditions.bet[type][index] = {
        minBetsCount: newTriggerConditions.bet[type][index].minBetsCount,
        maxBetsCount: newTriggerConditions.bet[type][index].maxBetsCount,
        [field]: value
      };
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
  };

  const addBetCondition = () => {
    setStore(prev => {
      const newTriggerConditions = JSON.parse(JSON.stringify(prev.formData.triggerConditions));
      const conditions = Array.isArray(newTriggerConditions.bet.all)
        ? newTriggerConditions.bet.all
        : [];

      newTriggerConditions.bet.all = [
        ...conditions,
        {
          minBetsCount: 0,
          maxBetsCount: 0
        }
      ];

      return {
        ...prev,
        formData: {
          ...prev.formData,
          triggerConditions: newTriggerConditions
        }
      };
    });
  };

  const bonusTypeOptions = [
    { label: 'All', value: 'all' },
    { label: 'Fiat', value: 'fiat' },
    { label: 'Crypto', value: 'crypto' }
  ];

  const comparisonOptions = [
    { label: 'Less than or equal (<=)', value: '<=' },
    { label: 'Equal (=)', value: '=' },
    { label: 'Greater than or equal (>=)', value: '>=' }
  ];

  const socialAccountOptions = [
    { label: 'VK', value: 'vk' },
    { label: 'Telegram', value: 'tg' },
    { label: 'OK', value: 'ok' },
    { label: 'Google', value: 'gl' },
    { label: 'Steam', value: 'st' },
    { label: 'Phone', value: 'phone' },
    { label: 'Email', value: 'email' }
  ];
  
  const dateRangeI18nStrings = {
    todayAriaLabel: "Today",
    nextMonthAriaLabel: "Next month",
    previousMonthAriaLabel: "Previous month",
    customRelativeRangeDurationLabel: "Duration",
    customRelativeRangeDurationPlaceholder: "Enter duration",
    customRelativeRangeOptionLabel: "Custom range",
    customRelativeRangeOptionDescription: "Define a custom range",
    customRelativeRangeUnitLabel: "Unit of time",
    formatRelativeRange: ({ startDate, endDate }) => {
      const start = new Date(startDate).toLocaleString("en-GB", {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
      const end = new Date(endDate).toLocaleString("en-GB", {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
      return `${start} - ${end} UTC`;
    },
    formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
    dateTimeConstraintText: "Date and time must be within range.",
    renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) =>
      `Selected from ${startDate} to ${endDate}`,
    labelRelativeRange: "Relative range",
    labelAbsoluteRange: "Absolute range",
    dateTimePickerAriaLabel: "Date and time picker",
    dateTimePickerLabel: "Date and Time",
    clearButtonAriaLabel: "Clear date range",
    relativeModeTitle: "Relative mode",
    absoluteModeTitle: "Absolute mode",
    cancelButtonLabel: "Cancel",
    applyButtonLabel: "Apply",
    startDateLabel: "Start date",
    startTimeLabel: "Start time",
    endDateLabel: "End date",
    endTimeLabel: "End time",
  };
  
  const dailyUtcWindows = getDailyUtcWindows();
  const mainUtcDate = getMainUtcDateString();
  const depositThresholdsForForm = normalizeDepositThresholds(store.formData.data);

  if (!store.isOpen) return null;

  return (
    <Modal
      visible={store.isOpen}
      onDismiss={handleClose}
      header={store.mode === 'add' ? 'Create Personal Promotion' : 'Edit Personal Promotion'}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              loading={store.loading}
            >
              {store.mode === 'add' ? 'Create Promotion' : 'Update Promotion'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        {store.error && (
          <Alert 
            type="error" 
            dismissible 
            onDismiss={() => setStore(prev => ({ ...prev, error: null }))}
          >
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {store.error}
            </div>
          </Alert>
        )}

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              label: "Basic Settings",
              id: "basic",
              content: (
                <SpaceBetween size="l">
                  <Container header={<Header>Promotion Settings</Header>}>
                    <ColumnLayout columns={2}>
                      <FormField
                        label="Internal name"
                        errorText={validationErrors.internal_name?.[0]}
                        constraintText="Required, up to 30 characters"
                      >
                        <Input
                          value={store.formData.internal_name || ''}
                          onChange={({ detail }) => updateFormField('internal_name', detail.value.slice(0, 30))}
                        />
                      </FormField>

                      <FormField
                        label="Internal description"
                        errorText={validationErrors.internal_description?.[0]}
                        constraintText="Optional, up to 120 characters"
                      >
                        <Textarea
                          value={store.formData.internal_description || ''}
                          onChange={({ detail }) => updateFormField('internal_description', detail.value.slice(0, 120))}
                        />
                      </FormField>
                    </ColumnLayout>

                    <FormField 
                      label="Promotion Validity Period (UTC)" 
                      constraintText={store.mode === 'add' 
                        ? "All dates and times are in UTC. Start must be today or later, end must be in the future."
                        : "All dates and times are displayed and saved in UTC timezone."}
                      errorText={validationErrors.dateRange?.[0]}
                    >
                        <DateRangePicker
                          onChange={({ detail }) => {
                          if (detail.value?.startDate && detail.value?.endDate) {
                            setStore(prev => ({ 
                              ...prev, 
                              formData: { 
                                ...prev.formData, 
                                startDate: detail.value.startDate,
                                endDate: detail.value.endDate
                              } 
                            }));
                            setValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.startDate;
                              delete newErrors.endDate;
                              delete newErrors.dateRange;
                              return newErrors;
                            });
                          }
                        }}
                        value={store.formData.startDate && store.formData.endDate ? {
                          startDate: store.formData.startDate,
                          endDate: store.formData.endDate,
                          type: "absolute"
                        } : null}
                        dateOnly={false}
                        timeInputFormat="hh:mm:ss"
													timeOffset={0}
                        rangeSelectorMode="absolute-only"
                        relativeOptions={[]}
                        getDateDisabledReason={date => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const selectedDate = new Date(date);
                          selectedDate.setHours(0, 0, 0, 0);
                          
                          if (store.mode === 'add' && selectedDate < today) {
                            return "Cannot select past dates for new promotions";
                          }
                          
                          return "";
                        }}
                        isDateEnabled={date => {
                          if (store.mode === 'edit') {
                            return true;
                          }
                          
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const selectedDate = new Date(date);
                          selectedDate.setHours(0, 0, 0, 0);
                          
                          return selectedDate >= today;
                        }}
                        isValidRange={range => {
                          if (range.type === "absolute") {
                            const [startDateWithoutTime] = range.startDate.split("T");
                            const [endDateWithoutTime] = range.endDate.split("T");
                            if (!startDateWithoutTime || !endDateWithoutTime) {
                              return {
                                valid: false,
                                errorMessage: "The selected date range is incomplete.",
                              };
                            }
                            
                            const startDate = new Date(range.startDate);
                            const endDate = new Date(range.endDate);
                            const now = new Date();
                            
                            if (startDate >= endDate) {
                              return {
                                valid: false,
                                errorMessage: "Start date must be before end date.",
                              };
                            }
                            
                            if (store.mode === 'add') {
                              const todayStart = new Date();
                              todayStart.setHours(0, 0, 0, 0);
                              
                              if (startDate < todayStart) {
                                return {
                                  valid: false,
                                  errorMessage: "Start date cannot be in the past. Please select today or a future date.",
                                };
                              }
                              
                              if (endDate <= now) {
                                return {
                                  valid: false,
                                  errorMessage: "End date must be in the future.",
                                };
                              }
                            }
                          }
                          return { valid: true };
                        }}
                        i18nStrings={dateRangeI18nStrings}
                        placeholder="Select date and time range (UTC)"
                      />
                    </FormField>
                    
                    <FormField
                      label="Daily UTC Windows (Optional)"
                      description={mainUtcDate
                        ? `Add up to 3 daily time windows in UTC. Main date: ${mainUtcDate}.`
                        : "Add up to 3 daily time windows in UTC. Select the main period to enable."}
                      constraintText="Use HH:mm. Windows must not overlap and start must be before end."
                      errorText={validationErrors.dailyUtcWindows?.[0]}
                    >
                      <SpaceBetween size="s">
                        {dailyUtcWindows.length === 0 ? (
                          <Box fontSize="body-s">No windows added.</Box>
                        ) : (
                          dailyUtcWindows.map((window, index) => (
                            <FormField
                              label={`Window #${index + 1} (UTC)`}
                              key={`daily-utc-window-${index}`}
                            >
                              <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                                <FormField label="Start time">
                                  <TimeInput
                                    value={window.start || ''}
                                    onChange={({ detail }) => setDailyUtcWindow(index, { start: detail.value })}
                                    format="hh:mm"
                                    placeholder="HH:mm"
                                    invalid={!!validationErrors.dailyUtcWindows}
                                    disabled={!store.formData.startDate || !store.formData.endDate}
                                  />
                                </FormField>
                                <FormField label="End time">
                                  <TimeInput
                                    value={window.end || ''}
                                    onChange={({ detail }) => setDailyUtcWindow(index, { end: detail.value })}
                                    format="hh:mm"
                                    placeholder="HH:mm"
                                    invalid={!!validationErrors.dailyUtcWindows}
                                    disabled={!store.formData.startDate || !store.formData.endDate}
                                  />
                                </FormField>
                                <Box margin={{ top: 'l' }} textAlign="right">
                                  <Button
                                    iconName="remove"
                                    onClick={() => removeDailyUtcWindow(index)}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </Grid>
                            </FormField>
                          ))
                        )}
                        <Button
                          onClick={addDailyUtcWindow}
                          disabled={
                            dailyUtcWindows.length >= 3 ||
                            !store.formData.startDate ||
                            !store.formData.endDate
                          }
                        >
                          Add window
                        </Button>
                      </SpaceBetween>
                    </FormField>
                    
                    <ColumnLayout columns={2}>
                      <FormField label="Type">
                        <Select
                          selectedOption={{ label: 'Money', value: 'money' }}
                          onChange={({ detail }) => setStore(prev => ({ ...prev, formData: { ...prev.formData, type: detail.selectedOption.value } }))}
                          options={[
                            { label: 'Money', value: 'money' },
                            { label: 'Freespins', value: 'freespins', disabled: true }
                          ]}
                        />
                      </FormField>

                      <FormField label="Status">
                        <Select
                          selectedOption={{ label: store.formData.status === 'active' ? 'Active' : 'Inactive', value: store.formData.status }}
                          onChange={({ detail }) => setStore(prev => ({ ...prev, formData: { ...prev.formData, status: detail.selectedOption.value } }))}
                          options={[
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' }
                          ]}
                        />
                      </FormField>
                    </ColumnLayout>
                    
                    <ColumnLayout columns={2}>
                      <FormField 
                        label="Promotion Lifetime (Minutes)"
                        description={`How long the promotion stays active after triggering. Max: 1440 minutes (24 hours)`}
                        errorText={validationErrors.data?.ttlMinutes?.[0]}
                      >
                        <Input
                          type="text"
                          value={store.formData.data.ttlMinutes?.toString() || '0'}
                          onChange={({ detail }) => {
                            const cleanValue = detail.value.replace(/\D/g, '');
                            const value = cleanValue !== '' ? parseInt(cleanValue) : 0;
                            updateData('ttlMinutes', value);
                            const errors = validateField('ttlMinutes', value, LIMITS.ttlMinutes);
                            if (errors.length > 0) {
                              setValidationErrors(prev => ({
                                ...prev,
                                data: { ...prev.data, ttlMinutes: errors }
                              }));
                            } else {
                              setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                if (newErrors.data) {
                                  delete newErrors.data.ttlMinutes;
                                }
                                return newErrors;
                              });
                            }
                          }}
                          placeholder="Enter amount"
                          invalid={!!validationErrors.data?.ttlMinutes}
                        />
                      </FormField>

                      <FormField
                        label="Priority"
                        description={
                          <>
                            Higher value means higher display priority
                            <br />
                            &nbsp;
                          </>
                        }
                      >
                        <Input
                          type="number"
                          value={store.formData.priority?.toString() || '0'}
                          onChange={({ detail }) =>
                            setStore(prev => ({
                              ...prev,
                              formData: {
                                ...prev.formData,
                                priority: detail.value === '' ? 0 : parseInt(detail.value, 10) || 0
                              }
                            }))
                          }
                          placeholder="0"
                        />
                      </FormField>
                    </ColumnLayout>
                  </Container>

                  <Container header={<Header>Promotion Image</Header>}>
                    <FormField 
                      label="Upload Image" 
                      constraintText="Required. Image dimensions must be 540x300 px. JPG format only."
                      errorText={validationErrors.image?.[0]}
                    >
                      <div style={{ 
                        border: validationErrors.image ? '2px dashed #d91515' : '1px dashed #d1d5db', 
                        borderRadius: '8px', 
                        padding: '16px',
                        textAlign: 'center',
                        backgroundColor: validationErrors.image 
                          ? (isDarkMode() ? '#3d1f1f' : '#fff5f5') 
                          : (isDarkMode() ? '#232f3e' : '#fafafa')
                      }}>
                        {store.formData.image && !store.formData.imageFile && (
                          <div style={{ marginBottom: '16px' }}>
                            <Badge color="blue">
                              {store.mode === 'edit' ? 'Current image: ' : 'Duplicated image: '}{store.formData.image}
                            </Badge>
                            <div style={{ marginTop: '8px', fontSize: '12px', color: isDarkMode() ? '#aaa' : '#666' }}>
                              {store.mode === 'edit' 
                                ? 'Upload a new image to replace the existing one'
                                : 'This image was copied from the original promotion.'}
                            </div>
                          </div>
                        )}
                        
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,.jpg,.jpeg,.JPG,.JPEG"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              if (!['image/jpeg', 'image/jpg'].includes(file.type)) {
                                setStore(prev => ({ ...prev, error: 'Only JPG images are allowed' }));
                                return;
                              }
                              
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setImagePreview(event.target.result);
                              };
                              reader.readAsDataURL(file);
                              
                              setStore(prev => ({ 
                                ...prev, 
                                formData: { 
                                  ...prev.formData, 
                                  imageFile: file,
                                  image: file.name 
                                }
                              }));
                              setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.image;
                                return newErrors;
                              });
                            } else {
                              setImagePreview(null);
                              if (store.mode === 'edit' || (store.mode === 'add' && store.formData.image)) {
                                setStore(prev => ({
                                  ...prev, 
                                  formData: { 
                                    ...prev.formData, 
                                    imageFile: null
                                  }
                                }));
                              } else {
                                setStore(prev => ({
                                  ...prev, 
                                  formData: { 
                                    ...prev.formData, 
                                    imageFile: null,
                                    image: '' 
                                  }
                                }));
                              }
                            }
                          }}
                          style={{ marginBottom: '8px' }}
                        />
                        
                        {store.formData.imageFile && (
                          <div style={{ marginTop: '8px' }}>
                            <Badge color="green">
                              New image selected: {store.formData.imageFile.name}
                            </Badge>
                          </div>
                        )}
                        
                        {imagePreview && (
                          <div style={{ 
                            marginTop: '16px',
                            padding: '8px',
                            backgroundColor: isDarkMode() ? '#1a1a1a' : '#fff',
                            borderRadius: '4px',
                            border: '1px solid ' + (isDarkMode() ? '#444' : '#ddd')
                          }}>
                            <div style={{ 
                              fontSize: '12px', 
                              marginBottom: '8px',
                              color: isDarkMode() ? '#aaa' : '#666',
                              fontWeight: 'bold'
                            }}>
                              Image Preview:
                            </div>
                            <img 
                              src={imagePreview} 
                              alt="Promotion preview" 
                              style={{ 
                                maxWidth: '100%', 
                                width: '540px',
                                height: 'auto',
                                borderRadius: '4px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }} 
                            />
                          </div>
                        )}
                        
                        {store.formData.image && !store.formData.imageFile && store.mode === 'edit' && (
                          <div style={{ 
                            marginTop: '16px',
                            padding: '8px',
                            backgroundColor: isDarkMode() ? '#1a1a1a' : '#fff',
                            borderRadius: '4px',
                            border: '1px solid ' + (isDarkMode() ? '#444' : '#ddd')
                          }}>
                            <div style={{ 
                              fontSize: '12px', 
                              marginBottom: '8px',
                              color: isDarkMode() ? '#aaa' : '#666',
                              fontWeight: 'bold'
                            }}>
                              Current Image:
                            </div>
                            {(isCdnLoading || imageLoading.current) ? (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '40px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px'
                              }}>
                                <Spinner size="large" />
                                <div style={{ color: isDarkMode() ? '#aaa' : '#666', fontSize: '14px' }}>
                                  Loading image...
                                </div>
                              </div>
                            ) : null}
                            {getImageUrl(store.formData.image) && !imageLoadError.current ? (
                              <img 
                                src={getImageUrl(store.formData.image)} 
                                alt="Current promotion" 
                                style={{ 
                                  maxWidth: '100%', 
                                  width: '540px',
                                  height: 'auto',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  display: imageLoading.current ? 'none' : 'block'
                                }}
                                onLoad={() => {
                                  setImageLoading(prev => ({ ...prev, current: false }));
                                }}
                                onError={() => {
                                  setImageLoadError(prev => ({ ...prev, current: true }));
                                  setImageLoading(prev => ({ ...prev, current: false }));
                                }}
                              />
                            ) : null}
                            {!isCdnLoading && !imageLoading.current && (imageLoadError.current || !getImageUrl(store.formData.image)) ? (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                Image not available
                              </div>
                            ) : null}
                          </div>
                        )}
                        
                        {store.formData.image && !store.formData.imageFile && store.mode === 'add' && (
                          <div style={{ 
                            marginTop: '16px',
                            padding: '8px',
                            backgroundColor: isDarkMode() ? '#1a1a1a' : '#fff',
                            borderRadius: '4px',
                            border: '1px solid ' + (isDarkMode() ? '#444' : '#ddd')
                          }}>
                            <div style={{ 
                              fontSize: '12px', 
                              marginBottom: '8px',
                              color: isDarkMode() ? '#aaa' : '#666',
                              fontWeight: 'bold'
                            }}>
                              Duplicated Image:
                            </div>
                            {(isCdnLoading || imageLoading.duplicated) ? (
                              <div style={{ 
                                textAlign: 'center', 
                                padding: '40px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px'
                              }}>
                                <Spinner size="large" />
                                <div style={{ color: isDarkMode() ? '#aaa' : '#666', fontSize: '14px' }}>
                                  Loading image...
                                </div>
                              </div>
                            ) : null}
                            {getImageUrl(store.formData.image) && !imageLoadError.duplicated ? (
                              <img 
                                src={getImageUrl(store.formData.image)} 
                                alt="Duplicated promotion" 
                                style={{ 
                                  maxWidth: '100%', 
                                  width: '540px',
                                  height: 'auto',
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  display: imageLoading.duplicated ? 'none' : 'block'
                                }}
                                onLoad={() => {
                                  setImageLoading(prev => ({ ...prev, duplicated: false }));
                                }}
                                onError={() => {
                                  setImageLoadError(prev => ({ ...prev, duplicated: true }));
                                  setImageLoading(prev => ({ ...prev, duplicated: false }));
                                }}
                              />
                            ) : null}
                            {!isCdnLoading && !imageLoading.duplicated && (imageLoadError.duplicated || !getImageUrl(store.formData.image)) ? (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                Image not available
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </FormField>
                  </Container>

                  <Container header={<Header>Bonus Configuration</Header>}>
                    <SpaceBetween size="m">
                      <FormField label="Bonus Type">
                        <Select
                          selectedOption={bonusTypeOptions.find(o => o.value === store.formData.data.bonusType)}
                          onChange={({ detail }) => updateData('bonusType', detail.selectedOption.value)}
                          options={bonusTypeOptions}
                        />
                      </FormField>

                      {validationErrors.data?.depositThresholds && (
                        <Alert type="error">{validationErrors.data.depositThresholds[0]}</Alert>
                      )}

                      {depositThresholdsForForm.map((threshold, index) => (
                        <Container
                          key={index}
                          header={
                            <Header
                              variant="h3"
                              actions={
                                <Button
                                  disabled={depositThresholdsForForm.length <= 1}
                                  onClick={() => removeDepositThreshold(index)}
                                >
                                  Remove
                                </Button>
                              }
                            >
                              Deposit Threshold #{index + 1}
                            </Header>
                          }
                        >
                          <SpaceBetween size="m">
                            <ColumnLayout columns={3}>
                              <FormField 
                                label="Min Deposit Amount" 
                                description={formatCurrency(threshold.minDepositAmount)}
                                errorText={validationErrors.data?.[`depositThresholds-${index}-minDepositAmount`]?.[0]}
                              >
                                <Input
                                  type="number"
                                  value={threshold.minDepositAmount.toString()}
                                  onChange={({ detail }) => {
                                    const value = parseInt(detail.value) || 0;
                                    updateDepositThreshold(index, 'minDepositAmount', value);
                                  }}
                                  placeholder="50000 (= 500 ₽)"
                                  invalid={!!validationErrors.data?.[`depositThresholds-${index}-minDepositAmount`]}
                                />
                              </FormField>

                              <FormField 
                                label="Max Deposit Amount"
                                description={formatCurrency(threshold.maxDepositAmount)}
                                errorText={validationErrors.data?.[`depositThresholds-${index}-maxDepositAmount`]?.[0]}
                              >
                                <Input
                                  type="number"
                                  value={threshold.maxDepositAmount.toString()}
                                  onChange={({ detail }) => {
                                    const value = parseInt(detail.value) || 0;
                                    updateDepositThreshold(index, 'maxDepositAmount', value);
                                  }}
                                  placeholder="10000000 (= 100,000 ₽)"
                                  invalid={!!validationErrors.data?.[`depositThresholds-${index}-maxDepositAmount`]}
                                />
                              </FormField>

                              <FormField 
                                label="Bonus Percent"
                                description={`${threshold.bonusPercentAmount}%`}
                                errorText={validationErrors.data?.[`depositThresholds-${index}-bonusPercentAmount`]?.[0]}
                              >
                                <Input
                                  type="number"
                                  value={threshold.bonusPercentAmount.toString()}
                                  onChange={({ detail }) => {
                                    const value = parseInt(detail.value) || 0;
                                    updateDepositThreshold(index, 'bonusPercentAmount', value);
                                  }}
                                  placeholder="50"
                                  invalid={!!validationErrors.data?.[`depositThresholds-${index}-bonusPercentAmount`]}
                                />
                              </FormField>
                            </ColumnLayout>

                            <Box variant="small" color="text-label">
                              Max bonus: {formatCurrency(getMaxBonusAmount(threshold))}
                            </Box>
                          </SpaceBetween>
                        </Container>
                      ))}

                      <Box>
                        <Button
                          disabled={depositThresholdsForForm.length >= 3}
                          onClick={addDepositThreshold}
                        >
                          Add Deposit Threshold
                        </Button>
                      </Box>
                    </SpaceBetween>
                  </Container>
                </SpaceBetween>
              )
            },
            {
              label: "Trigger Conditions",
              id: "triggers",
              content: (
                <SpaceBetween size="l">
                  <Container header={<Header>Balance Guard</Header>}>
                    <SpaceBetween size="m">
                      <ColumnLayout columns={2}>
                        <FormField 
                            label="Min Current Bet Amount"
                            description={store.formData.triggerConditions.balanceGuard.minCurrentBetAmount === 0 ? 
                              'Any bet amount' : 
                              formatCurrency(store.formData.triggerConditions.balanceGuard.minCurrentBetAmount || 10000)}
                            errorText={validationErrors.balanceGuard?.minCurrentBetAmount?.[0]}
                          >
                            <Input
                              type="number"
                              value={store.formData.triggerConditions.balanceGuard.minCurrentBetAmount?.toString() || '10000'}
                              onChange={({ detail }) => {
                                const value = detail.value !== '' ? parseInt(detail.value) || 0 : 0;
                                updateTriggerConditions('balanceGuard.minCurrentBetAmount', value);
                                const errors = validateField('minCurrentBetAmount', value, LIMITS.depositAmount);
                                if (errors.length > 0) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    balanceGuard: { ...prev.balanceGuard, minCurrentBetAmount: errors }
                                  }));
                                } else {
                                  setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.balanceGuard) {
                                      delete newErrors.balanceGuard.minCurrentBetAmount;
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              placeholder="10000"
                              invalid={!!validationErrors.balanceGuard?.minCurrentBetAmount}
                            />
                          </FormField>

                          <FormField 
                            label="Remaining Bets Multiplier"
                            description="Trigger when balance ≤ (bet × multiplier). Default: 5"
                            errorText={validationErrors.balanceGuard?.remainingBetsMultiplier?.[0]}
                          >
                            <Input
                              type="number"
                              value={store.formData.triggerConditions.balanceGuard.remainingBetsMultiplier?.toString() || '5'}
                              onChange={({ detail }) => {
                                const value = detail.value !== '' ? parseFloat(detail.value) || 0 : 0;
                                updateTriggerConditions('balanceGuard.remainingBetsMultiplier', value);
                                const errors = validateField('remainingBetsMultiplier', value, LIMITS.betsMultiplier);
                                if (errors.length > 0) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    balanceGuard: { ...prev.balanceGuard, remainingBetsMultiplier: errors }
                                  }));
                                } else {
                                  setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.balanceGuard) {
                                      delete newErrors.balanceGuard.remainingBetsMultiplier;
                                    }
                                    return newErrors;
                                  });
                                }
                              }}
                              placeholder="5"
                              invalid={!!validationErrors.balanceGuard?.remainingBetsMultiplier}
                            />
                          </FormField>

                          <FormField label="Comparison Operator" description="How to compare remaining balance">
                            <Select
                              selectedOption={comparisonOptions.find(o => o.value === store.formData.triggerConditions.balanceGuard.comparison) || comparisonOptions[0]}
                              onChange={({ detail }) => updateTriggerConditions('balanceGuard.comparison', detail.selectedOption.value)}
                              options={comparisonOptions}
                            />
                          </FormField>

                          <FormField 
                            label="Check After Placing Current Bet"
                            description={store.formData.triggerConditions.balanceGuard.checkAfterPlacingCurrentBet 
                              ? "Check balance AFTER deducting current bet" 
                              : "Check current balance WITHOUT deducting bet"}
                          >
                            <Toggle
                              checked={store.formData.triggerConditions.balanceGuard.checkAfterPlacingCurrentBet}
                              onChange={({ detail }) => updateTriggerConditions('balanceGuard.checkAfterPlacingCurrentBet', detail.checked)}
                            />
                          </FormField>
                        </ColumnLayout>
                    </SpaceBetween>
                  </Container>

                  <Container header={<Header>Account Requirements</Header>}>
                    <ColumnLayout columns={2}>
                      <FormField 
                        label="Min Account Age (days)" 
                        description="Leave empty for no minimum. Max: 8000 days"
                        errorText={validationErrors.account?.minAccountAgeDays?.[0]}
                      >
                        <SpaceBetween direction="horizontal" size="xs">
                          <Input
                            type="text"
                            value={store.formData.triggerConditions.account.minAccountAgeDays?.toString() || ''}
                            onChange={({ detail }) => {
                              const cleanValue = detail.value.replace(/\D/g, '');
                              const value = cleanValue ? parseInt(cleanValue) : undefined;
                              updateTriggerConditions('account.minAccountAgeDays', value);
                              
                              if (value !== undefined) {
                                const errors = validateField('minAccountAgeDays', value, LIMITS.accountAge);
                                const maxAge = store.formData.triggerConditions.account.maxAccountAgeDays;
                                
                                if (errors.length > 0) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    account: { ...prev.account, minAccountAgeDays: errors }
                                  }));
                                } else if (maxAge !== undefined && value > maxAge) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    account: { 
                                      ...prev.account, 
                                      minAccountAgeDays: ['Minimum age cannot be greater than maximum age'],
                                      maxAccountAgeDays: ['Maximum age cannot be less than minimum age']
                                    }
                                  }));
                                } else {
                                  setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.account) {
                                      delete newErrors.account.minAccountAgeDays;
                                      if (maxAge === undefined || value <= maxAge) {
                                        delete newErrors.account.maxAccountAgeDays;
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              } else {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors.account) {
                                    delete newErrors.account.minAccountAgeDays;
                                    delete newErrors.account.maxAccountAgeDays;
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="e.g. 7 for week old accounts"
                            invalid={!!validationErrors.account?.minAccountAgeDays}
                          />
                          <Button
                            variant="inline-link"
                            onClick={() => {
                              updateTriggerConditions('account.minAccountAgeDays', LIMITS.accountAge.max);
                              setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                if (newErrors.account) {
                                  delete newErrors.account.minAccountAgeDays;
                                }
                                return newErrors;
                              });
                            }}
                          >
                            Max
                          </Button>
                        </SpaceBetween>
                      </FormField>

                      <FormField 
                        label="Max Account Age (days)"
                        description="Leave empty for no maximum. Max: 8000 days"
                        errorText={validationErrors.account?.maxAccountAgeDays?.[0]}
                      >
                        <SpaceBetween direction="horizontal" size="xs">
                          <Input
                            type="text"
                            value={store.formData.triggerConditions.account.maxAccountAgeDays?.toString() || ''}
                            onChange={({ detail }) => {
                              const cleanValue = detail.value.replace(/\D/g, '');
                              const value = cleanValue ? parseInt(cleanValue) : undefined;
                              updateTriggerConditions('account.maxAccountAgeDays', value);
                              
                              if (value !== undefined) {
                                const errors = validateField('maxAccountAgeDays', value, LIMITS.accountAge);
                                const minAge = store.formData.triggerConditions.account.minAccountAgeDays;
                                
                                if (errors.length > 0) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    account: { ...prev.account, maxAccountAgeDays: errors }
                                  }));
                                } else if (minAge !== undefined && value < minAge) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    account: { 
                                      ...prev.account, 
                                      minAccountAgeDays: ['Minimum age cannot be greater than maximum age'],
                                      maxAccountAgeDays: ['Maximum age cannot be less than minimum age']
                                    }
                                  }));
                                } else {
                                  setValidationErrors(prev => {
                                    const newErrors = { ...prev };
                                    if (newErrors.account) {
                                      delete newErrors.account.maxAccountAgeDays;
                                      if (minAge === undefined || value >= minAge) {
                                        delete newErrors.account.minAccountAgeDays;
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                              } else {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  if (newErrors.account) {
                                    delete newErrors.account.maxAccountAgeDays;
                                    delete newErrors.account.minAccountAgeDays;
                                  }
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="e.g. 30 for new users only"
                            invalid={!!validationErrors.account?.maxAccountAgeDays}
                          />
                          <Button
                            variant="inline-link"
                            onClick={() => {
                              updateTriggerConditions('account.maxAccountAgeDays', LIMITS.accountAge.max);
                              setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                if (newErrors.account) {
                                  delete newErrors.account.maxAccountAgeDays;
                                }
                                return newErrors;
                              });
                            }}
                          >
                            Max
                          </Button>
                        </SpaceBetween>
                      </FormField>

                      <FormField 
                        label="Required Social Accounts"
                        description="Optional: User must have these accounts linked"
                      >
                        <Multiselect
                          selectedOptions={store.formData.triggerConditions.account.requiredSocialAccounts?.map(v => socialAccountOptions.find(o => o.value === v)) || []}
                          onChange={({ detail }) => updateTriggerConditions('account.requiredSocialAccounts', detail.selectedOptions.map(o => o.value))}
                          options={socialAccountOptions}
                          placeholder="None required"
                        />
                      </FormField>

                      <FormField
                        label="Risk model"
                        description="Leave empty for no risk model"
                     //   errorText={validationErrors.account?.minAccountAgeDays?.[0]}
                      >
                        <Multiselect
                          selectedOptions={getRiskModelSelectedOptions(store.formData.triggerConditions.account?.riskModel)}
                          onChange={({ detail }) => {
                            updateTriggerConditions('account.riskModel', detail.selectedOptions.map(option => option.value));
                          }}
                          options={RISK_MODEL_OPTIONS}
                          placeholder="Select risk models"
                          selectedAriaLabel="Selected"
                        />
                      </FormField>
                    </ColumnLayout>
                  </Container>

                  <Container header={<Header>Deposit Conditions</Header>}>
                    <SpaceBetween size="m">
                      {validationErrors.depositConditions && (
                        <Alert type="error">
                          {validationErrors.depositConditions[0]}
                        </Alert>
                      )}
                      {!store.formData.triggerConditions.deposit?.all?.length &&
                       !store.formData.triggerConditions.deposit?.fiat?.length && 
                       !store.formData.triggerConditions.deposit?.crypto?.length && (
                        <Container header={<Header variant="h3">Configure Deposit Conditions</Header>}>
                          <SpaceBetween direction="horizontal" size="m">
                            <Button 
                              onClick={() => {
                                setStore(prev => ({
                                  ...prev,
                                  formData: {
                                    ...prev.formData,
                                    triggerConditions: {
                                      ...prev.formData.triggerConditions,
                                      deposit: {
                                        all: []
                                      }
                                    }
                                  }
                                }));
                              }}
                              variant="primary"
                            >
                              Add All Deposits Together
                            </Button>
                            <Button 
                              onClick={() => {
                                setStore(prev => ({
                                  ...prev,
                                  formData: {
                                    ...prev.formData,
                                    triggerConditions: {
                                      ...prev.formData.triggerConditions,
                                      deposit: {
                                        fiat: [],
                                        crypto: []
                                      }
                                    }
                                  }
                                }));
                              }}
                            >
                              Add Separate Fiat/Crypto
                            </Button>
                          </SpaceBetween>
                        </Container>
                      )}
                      {store.formData.triggerConditions.deposit?.all && (
                        <Container header={
                          <Header 
                            variant="h2"
                            description="Apply conditions to all deposit types combined"
                            actions={
                              <Button 
                                onClick={() => {
                                  setStore(prev => ({
                                    ...prev,
                                    formData: {
                                      ...prev.formData,
                                      triggerConditions: {
                                        ...prev.formData.triggerConditions,
                                        deposit: {}
                                      }
                                    }
                                  }));
                                }}
                                variant="inline-link"
                                iconName="remove"
                              >
                                Remove All Deposits Mode
                              </Button>
                            }
                          >
                            All Deposits Together
                          </Header>
                        }>
                          <SpaceBetween size="m">
                            <Button 
                              onClick={() => {
                                updateDepositCondition('all', (store.formData.triggerConditions.deposit.all?.length || 0), 'minDepositsAmount', 0);
                              }}
                            >
                              Add Condition
                            </Button>
                            {store.formData.triggerConditions.deposit.all?.map((condition, index) => (
                              <Container key={index}>
                                <SpaceBetween size="m">
                                  <ColumnLayout columns={2}>
                                    <FormField 
                                      label="Min Deposits Amount"
                                      description={`${formatCurrency(condition.minDepositsAmount || 0)} • Min: ${formatCurrency(LIMITS.depositsAmount.min)}, Max: ${formatCurrency(LIMITS.depositsAmount.max)}`}
                                      errorText={validationErrors.deposit?.[`all-${index}-minDepositsAmount`]?.[0]}
                                    >
                                      <Input
                                        type="number"
                                        value={condition.minDepositsAmount?.toString() || '0'}
                                        onChange={({ detail }) => {
                                          const value = parseInt(detail.value) || 0;
                                          if (value > LIMITS.depositsAmount.max) {
                                            setStore(prev => ({ ...prev, error: `Maximum value is ${formatCurrency(LIMITS.depositsAmount.max)}` }));
                                            return;
                                          }
                                          let hasError = false;
                                          
                                          if (value < LIMITS.depositsAmount.min) {
                                            setValidationErrors(prev => ({
                                              ...prev,
                                              deposit: { 
                                                ...prev.deposit, 
                                                [`all-${index}-minDepositsAmount`]: [`Minimum value is ${formatCurrency(LIMITS.depositsAmount.min)}`]
                                              }
                                            }));
                                            hasError = true;
                                          }
                                          
                                          if (!hasError) {
                                            const maxAmount = store.formData.triggerConditions.deposit.all?.[index]?.maxDepositsAmount;
                                            if (maxAmount !== undefined && value > maxAmount) {
                                              setValidationErrors(prev => ({
                                                ...prev,
                                                deposit: { 
                                                  ...prev.deposit, 
                                                  [`all-${index}-minDepositsAmount`]: ['Minimum amount cannot be greater than maximum amount'],
                                                  [`all-${index}-maxDepositsAmount`]: ['Maximum amount cannot be less than minimum amount']
                                                }
                                              }));
                                            } else {
                                              setValidationErrors(prev => {
                                                const newErrors = { ...prev };
                                                if (newErrors.deposit) {
                                                  delete newErrors.deposit[`all-${index}-minDepositsAmount`];
                                                  if (maxAmount === undefined || value <= maxAmount) {
                                                    delete newErrors.deposit[`all-${index}-maxDepositsAmount`];
                                                  }
                                                }
                                                return newErrors;
                                              });
                                            }
                                          }
                                          
                                          updateDepositCondition('all', index, 'minDepositsAmount', value);
                                        }}
                                        invalid={!!validationErrors.deposit?.[`all-${index}-minDepositsAmount`]}
                                      />
                                    </FormField>
                                    <FormField 
                                      label="Max Deposits Amount"
                                      description={`${formatCurrency(condition.maxDepositsAmount || 0)} • Min: ${formatCurrency(LIMITS.depositsAmount.min)}, Max: ${formatCurrency(LIMITS.depositsAmount.max)}`}
                                      errorText={validationErrors.deposit?.[`all-${index}-maxDepositsAmount`]?.[0]}
                                    >
                                      <Input
                                        type="number"
                                        value={condition.maxDepositsAmount?.toString() || '0'}
                                        onChange={({ detail }) => {
                                          const value = parseInt(detail.value) || 0;
                                          if (value > LIMITS.depositsAmount.max) {
                                            setStore(prev => ({ ...prev, error: `Maximum value is ${formatCurrency(LIMITS.depositsAmount.max)}` }));
                                            return;
                                          }
                                          let hasError = false;
                                          
                                          if (value < LIMITS.depositsAmount.min) {
                                            setValidationErrors(prev => ({
                                              ...prev,
                                              deposit: { 
                                                ...prev.deposit, 
                                                [`all-${index}-maxDepositsAmount`]: [`Minimum value is ${formatCurrency(LIMITS.depositsAmount.min)}`]
                                              }
                                            }));
                                            hasError = true;
                                          }
                                          
                                          if (!hasError) {
                                            const minAmount = store.formData.triggerConditions.deposit.all?.[index]?.minDepositsAmount;
                                            if (minAmount !== undefined && value < minAmount) {
                                              setValidationErrors(prev => ({
                                                ...prev,
                                                deposit: { 
                                                  ...prev.deposit, 
                                                  [`all-${index}-minDepositsAmount`]: ['Minimum amount cannot be greater than maximum amount'],
                                                  [`all-${index}-maxDepositsAmount`]: ['Maximum amount cannot be less than minimum amount']
                                                }
                                              }));
                                            } else {
                                              setValidationErrors(prev => {
                                                const newErrors = { ...prev };
                                                if (newErrors.deposit) {
                                                  delete newErrors.deposit[`all-${index}-maxDepositsAmount`];
                                                  if (minAmount === undefined || value >= minAmount) {
                                                    delete newErrors.deposit[`all-${index}-minDepositsAmount`];
                                                  }
                                                }
                                                return newErrors;
                                              });
                                            }
                                          }
                                          
                                          updateDepositCondition('all', index, 'maxDepositsAmount', value);
                                        }}
                                        invalid={!!validationErrors.deposit?.[`all-${index}-maxDepositsAmount`]}
                                      />
                                    </FormField>
                                    <FormField 
                                      label="Min Deposits Count"
                                      description={`Max: ${LIMITS.depositsCount.max.toLocaleString()}`}
                                      errorText={validationErrors.deposit?.[`all-${index}-minDepositsCount`]?.[0]}
                                    >
                                      <Input
                                        type="number"
                                        value={condition.minDepositsCount?.toString() || '0'}
                                        onChange={({ detail }) => {
                                          const value = parseInt(detail.value) || 0;
                                          if (value > LIMITS.depositsCount.max) {
                                            setStore(prev => ({ ...prev, error: `Maximum value is ${LIMITS.depositsCount.max.toLocaleString()}` }));
                                            return;
                                          }
                                          
                                          const maxCount = store.formData.triggerConditions.deposit.all?.[index]?.maxDepositsCount;
                                          if (maxCount !== undefined && value > maxCount) {
                                            setValidationErrors(prev => ({
                                              ...prev,
                                              deposit: { 
                                                ...prev.deposit, 
                                                [`all-${index}-minDepositsCount`]: ['Minimum count cannot be greater than maximum count'],
                                                [`all-${index}-maxDepositsCount`]: ['Maximum count cannot be less than minimum count']
                                              }
                                            }));
                                          } else {
                                            setValidationErrors(prev => {
                                              const newErrors = { ...prev };
                                              if (newErrors.deposit) {
                                                delete newErrors.deposit[`all-${index}-minDepositsCount`];
                                                if (maxCount === undefined || value <= maxCount) {
                                                  delete newErrors.deposit[`all-${index}-maxDepositsCount`];
                                                }
                                              }
                                              return newErrors;
                                            });
                                          }
                                          
                                          updateDepositCondition('all', index, 'minDepositsCount', value);
                                        }}
                                        invalid={!!validationErrors.deposit?.[`all-${index}-minDepositsCount`]}
                                      />
                                    </FormField>
                                    <FormField 
                                      label="Max Deposits Count"
                                      description={`Max: ${LIMITS.depositsCount.max.toLocaleString()}`}
                                      errorText={validationErrors.deposit?.[`all-${index}-maxDepositsCount`]?.[0]}
                                    >
                                      <Input
                                        type="number"
                                        value={condition.maxDepositsCount?.toString() || '0'}
                                        onChange={({ detail }) => {
                                          const value = parseInt(detail.value) || 0;
                                          if (value > LIMITS.depositsCount.max) {
                                            setStore(prev => ({ ...prev, error: `Maximum value is ${LIMITS.depositsCount.max.toLocaleString()}` }));
                                            return;
                                          }
                                          
                                          const minCount = store.formData.triggerConditions.deposit.all?.[index]?.minDepositsCount;
                                          if (minCount !== undefined && value < minCount) {
                                            setValidationErrors(prev => ({
                                              ...prev,
                                              deposit: { 
                                                ...prev.deposit, 
                                                [`all-${index}-minDepositsCount`]: ['Minimum count cannot be greater than maximum count'],
                                                [`all-${index}-maxDepositsCount`]: ['Maximum count cannot be less than minimum count']
                                              }
                                            }));
                                          } else {
                                            setValidationErrors(prev => {
                                              const newErrors = { ...prev };
                                              if (newErrors.deposit) {
                                                delete newErrors.deposit[`all-${index}-maxDepositsCount`];
                                                if (minCount === undefined || value >= minCount) {
                                                  delete newErrors.deposit[`all-${index}-minDepositsCount`];
                                                }
                                              }
                                              return newErrors;
                                            });
                                          }
                                          
                                          updateDepositCondition('all', index, 'maxDepositsCount', value);
                                        }}
                                        invalid={!!validationErrors.deposit?.[`all-${index}-maxDepositsCount`]}
                                      />
                                    </FormField>
                                    <FormField 
                                      label="Period (days)"
                                      description="Leave empty for all time. Max: 365 days"
                                    >
                                      <Input
                                        type="text"
                                        value={condition.periodDays?.toString() || ''}
                                        onChange={({ detail }) => {
                                          const cleanValue = detail.value.replace(/\D/g, '');
                                          if (cleanValue === '') {
                                            updateDepositCondition('all', index, 'periodDays', undefined);
                                            return;
                                          }
                                          
                                          const value = parseInt(cleanValue);
                                          if (value > LIMITS.periodDays.max) {
                                            setStore(prev => ({ ...prev, error: `Maximum period is ${LIMITS.periodDays.max} days` }));
                                            return;
                                          }
                                          
                                          updateDepositCondition('all', index, 'periodDays', value);
                                        }}
                                        placeholder="Leave empty for all time"
                                      />
                                    </FormField>
                                  </ColumnLayout>
                                  <Box float="right">
                                    <Button 
                                      variant="link" 
                                      onClick={() => {
                                        const newDeposit = {...store.formData.triggerConditions.deposit};
                                        newDeposit.all = newDeposit.all.filter((_, i) => i !== index);
                                        setStore(prev => ({
                                          ...prev,
                                          formData: {
                                            ...prev.formData,
                                            triggerConditions: {
                                              ...prev.formData.triggerConditions,
                                              deposit: newDeposit
                                            }
                                          }
                                        }));
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  </Box>
                                </SpaceBetween>
                              </Container>
                            ))}
                          </SpaceBetween>
                        </Container>
                      )}

                      {(store.formData.triggerConditions.deposit?.fiat !== undefined ||
                        store.formData.triggerConditions.deposit?.crypto !== undefined) && (
                        <Container header={
                          <Header 
                            variant="h2"
                            description="Set different conditions for Fiat and Crypto deposits"
                            actions={
                              <Button 
                                onClick={() => {
                                  setStore(prev => ({
                                    ...prev,
                                    formData: {
                                      ...prev.formData,
                                      triggerConditions: {
                                        ...prev.formData.triggerConditions,
                                        deposit: {}
                                      }
                                    }
                                  }));
                                }}
                                variant="inline-link"
                                iconName="remove"
                              >
                                Remove Separate Mode
                              </Button>
                            }
                          >
                            Separate Fiat/Crypto Conditions
                          </Header>
                        }>
                          <>
                          {['fiat', 'crypto'].map(type => (
                            <ExpandableSection key={type} headerText={`${type.charAt(0).toUpperCase() + type.slice(1)} Deposits`}>
                              <SpaceBetween size="m">
                                <Button 
                                  onClick={() => {
                                    updateDepositCondition(type, (store.formData.triggerConditions.deposit[type]?.length || 0), 'minDepositsAmount', 0);
                                  }}
                                >
                                  Add Condition
                                </Button>
                                {store.formData.triggerConditions.deposit[type]?.map((condition, index) => (
                                  <Container key={index}>
                                    <SpaceBetween size="m">
                                      <ColumnLayout columns={2}>
                                        <FormField 
                                          label="Min Deposits Amount"
                                          description={`${formatCurrency(condition.minDepositsAmount || 0)} • Min: ${formatCurrency(LIMITS.depositsAmount.min)}, Max: ${formatCurrency(LIMITS.depositsAmount.max)}`}
                                          errorText={validationErrors.deposit?.[`${type}-${index}-minDepositsAmount`]?.[0]}
                                        >
                                          <Input
                                            type="number"
                                            value={condition.minDepositsAmount?.toString() || '0'}
                                            onChange={({ detail }) => {
                                              const value = parseInt(detail.value) || 0;
                                              if (value > LIMITS.depositsAmount.max) {
                                                setStore(prev => ({ ...prev, error: `Maximum value is ${formatCurrency(LIMITS.depositsAmount.max)}` }));
                                                return;
                                              }
                                              let hasError = false;
                                              
                                              if (value < LIMITS.depositsAmount.min) {
                                                setValidationErrors(prev => ({
                                                  ...prev,
                                                  deposit: { 
                                                    ...prev.deposit, 
                                                    [`${type}-${index}-minDepositsAmount`]: [`Minimum value is ${formatCurrency(LIMITS.depositsAmount.min)}`]
                                                  }
                                                }));
                                                hasError = true;
                                              }
                                              
                                              if (!hasError) {
                                                const maxAmount = store.formData.triggerConditions.deposit[type]?.[index]?.maxDepositsAmount;
                                                if (maxAmount !== undefined && value > maxAmount) {
                                                  setValidationErrors(prev => ({
                                                    ...prev,
                                                    deposit: { 
                                                      ...prev.deposit, 
                                                      [`${type}-${index}-minDepositsAmount`]: ['Minimum amount cannot be greater than maximum amount'],
                                                      [`${type}-${index}-maxDepositsAmount`]: ['Maximum amount cannot be less than minimum amount']
                                                    }
                                                  }));
                                                } else {
                                                  setValidationErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    if (newErrors.deposit) {
                                                      delete newErrors.deposit[`${type}-${index}-minDepositsAmount`];
                                                      if (maxAmount === undefined || value <= maxAmount) {
                                                        delete newErrors.deposit[`${type}-${index}-maxDepositsAmount`];
                                                      }
                                                    }
                                                    return newErrors;
                                                  });
                                                }
                                              }
                                              
                                              updateDepositCondition(type, index, 'minDepositsAmount', value);
                                            }}
                                            invalid={!!validationErrors.deposit?.[`${type}-${index}-minDepositsAmount`]}
                                          />
                                        </FormField>
                                        <FormField 
                                          label="Max Deposits Amount"
                                          description={`${formatCurrency(condition.maxDepositsAmount || 0)} • Min: ${formatCurrency(LIMITS.depositsAmount.min)}, Max: ${formatCurrency(LIMITS.depositsAmount.max)}`}
                                          errorText={validationErrors.deposit?.[`${type}-${index}-maxDepositsAmount`]?.[0]}
                                        >
                                          <Input
                                            type="number"
                                            value={condition.maxDepositsAmount?.toString() || '0'}
                                            onChange={({ detail }) => {
                                              const value = parseInt(detail.value) || 0;
                                              if (value > LIMITS.depositsAmount.max) {
                                                setStore(prev => ({ ...prev, error: `Maximum value is ${formatCurrency(LIMITS.depositsAmount.max)}` }));
                                                return;
                                              }
                                              let hasError = false;
                                              
                                              if (value < LIMITS.depositsAmount.min) {
                                                setValidationErrors(prev => ({
                                                  ...prev,
                                                  deposit: { 
                                                    ...prev.deposit, 
                                                    [`${type}-${index}-maxDepositsAmount`]: [`Minimum value is ${formatCurrency(LIMITS.depositsAmount.min)}`]
                                                  }
                                                }));
                                                hasError = true;
                                              }
                                              
                                              if (!hasError) {
                                                const minAmount = store.formData.triggerConditions.deposit[type]?.[index]?.minDepositsAmount;
                                                if (minAmount !== undefined && value < minAmount) {
                                                  setValidationErrors(prev => ({
                                                    ...prev,
                                                    deposit: { 
                                                      ...prev.deposit, 
                                                      [`${type}-${index}-minDepositsAmount`]: ['Minimum amount cannot be greater than maximum amount'],
                                                      [`${type}-${index}-maxDepositsAmount`]: ['Maximum amount cannot be less than minimum amount']
                                                    }
                                                  }));
                                                } else {
                                                  setValidationErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    if (newErrors.deposit) {
                                                      delete newErrors.deposit[`${type}-${index}-maxDepositsAmount`];
                                                      if (minAmount === undefined || value >= minAmount) {
                                                        delete newErrors.deposit[`${type}-${index}-minDepositsAmount`];
                                                      }
                                                    }
                                                    return newErrors;
                                                  });
                                                }
                                              }
                                              
                                              updateDepositCondition(type, index, 'maxDepositsAmount', value);
                                            }}
                                            invalid={!!validationErrors.deposit?.[`${type}-${index}-maxDepositsAmount`]}
                                          />
                                        </FormField>
                                        <FormField 
                                          label="Min Deposits Count"
                                          description={`Max: ${LIMITS.depositsCount.max.toLocaleString()}`}
                                          errorText={validationErrors.deposit?.[`${type}-${index}-minDepositsCount`]?.[0]}
                                        >
                                          <Input
                                            type="number"
                                            value={condition.minDepositsCount?.toString() || '0'}
                                            onChange={({ detail }) => {
                                              const value = parseInt(detail.value) || 0;
                                              if (value > LIMITS.depositsCount.max) {
                                                setStore(prev => ({ ...prev, error: `Maximum value is ${LIMITS.depositsCount.max.toLocaleString()}` }));
                                                return;
                                              }
                                              
                                              const maxCount = store.formData.triggerConditions.deposit[type]?.[index]?.maxDepositsCount;
                                              if (maxCount !== undefined && value > maxCount) {
                                                setValidationErrors(prev => ({
                                                  ...prev,
                                                  deposit: { 
                                                    ...prev.deposit, 
                                                    [`${type}-${index}-minDepositsCount`]: ['Minimum count cannot be greater than maximum count'],
                                                    [`${type}-${index}-maxDepositsCount`]: ['Maximum count cannot be less than minimum count']
                                                  }
                                                }));
                                              } else {
                                                setValidationErrors(prev => {
                                                  const newErrors = { ...prev };
                                                  if (newErrors.deposit) {
                                                    delete newErrors.deposit[`${type}-${index}-minDepositsCount`];
                                                    if (maxCount === undefined || value <= maxCount) {
                                                      delete newErrors.deposit[`${type}-${index}-maxDepositsCount`];
                                                    }
                                                  }
                                                  return newErrors;
                                                });
                                              }
                                              
                                              updateDepositCondition(type, index, 'minDepositsCount', value);
                                            }}
                                            invalid={!!validationErrors.deposit?.[`${type}-${index}-minDepositsCount`]}
                                          />
                                        </FormField>
                                        <FormField 
                                          label="Max Deposits Count"
                                          description={`Max: ${LIMITS.depositsCount.max.toLocaleString()}`}
                                          errorText={validationErrors.deposit?.[`${type}-${index}-maxDepositsCount`]?.[0]}
                                        >
                                          <Input
                                            type="number"
                                            value={condition.maxDepositsCount?.toString() || '0'}
                                            onChange={({ detail }) => {
                                              const value = parseInt(detail.value) || 0;
                                              if (value > LIMITS.depositsCount.max) {
                                                setStore(prev => ({ ...prev, error: `Maximum value is ${LIMITS.depositsCount.max.toLocaleString()}` }));
                                                return;
                                              }
                                              
                                              const minCount = store.formData.triggerConditions.deposit[type]?.[index]?.minDepositsCount;
                                              if (minCount !== undefined && value < minCount) {
                                                setValidationErrors(prev => ({
                                                  ...prev,
                                                  deposit: { 
                                                    ...prev.deposit, 
                                                    [`${type}-${index}-minDepositsCount`]: ['Minimum count cannot be greater than maximum count'],
                                                    [`${type}-${index}-maxDepositsCount`]: ['Maximum count cannot be less than minimum count']
                                                  }
                                                }));
                                              } else {
                                                setValidationErrors(prev => {
                                                  const newErrors = { ...prev };
                                                  if (newErrors.deposit) {
                                                    delete newErrors.deposit[`${type}-${index}-maxDepositsCount`];
                                                    if (minCount === undefined || value >= minCount) {
                                                      delete newErrors.deposit[`${type}-${index}-minDepositsCount`];
                                                    }
                                                  }
                                                  return newErrors;
                                                });
                                              }
                                              
                                              updateDepositCondition(type, index, 'maxDepositsCount', value);
                                            }}
                                            invalid={!!validationErrors.deposit?.[`${type}-${index}-maxDepositsCount`]}
                                          />
                                        </FormField>
                                        <FormField 
                                          label="Period (days)"
                                          description="Leave empty for all time. Max: 365 days"
                                        >
                                          <Input
                                            type="text"
                                            value={condition.periodDays?.toString() || ''}
                                            onChange={({ detail }) => {
                                              const cleanValue = detail.value.replace(/\D/g, '');
                                              if (cleanValue === '') {
                                                updateDepositCondition(type, index, 'periodDays', undefined);
                                                return;
                                              }
                                              
                                              const value = parseInt(cleanValue);
                                              if (value > LIMITS.periodDays.max) {
                                                setStore(prev => ({ ...prev, error: `Maximum period is ${LIMITS.periodDays.max} days` }));
                                                return;
                                              }
                                              
                                              updateDepositCondition(type, index, 'periodDays', value);
                                            }}
                                            placeholder="Leave empty for all time"
                                          />
                                        </FormField>
                                      </ColumnLayout>
                                      <Box float="right">
                                        <Button 
                                          variant="link" 
                                          onClick={() => {
                                            const newDeposit = {...store.formData.triggerConditions.deposit};
                                            newDeposit[type] = newDeposit[type].filter((_, i) => i !== index);
                                            setStore(prev => ({
                                              ...prev,
                                              formData: {
                                                ...prev.formData,
                                                triggerConditions: {
                                                  ...prev.formData.triggerConditions,
                                                  deposit: newDeposit
                                                }
                                              }
                                            }));
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      </Box>
                                    </SpaceBetween>
                                  </Container>
                                ))}
                              </SpaceBetween>
                            </ExpandableSection>
                          ))}
                        </>
                        </Container>
                      )}
                    </SpaceBetween>
                  </Container>

                  <Container header={<Header>Bet Conditions</Header>}>
                    <SpaceBetween size="m">
                      <Button onClick={addBetCondition}>
                        Add Bet Condition
                      </Button>
                      {store.formData.triggerConditions.bet.all?.map((condition, index) => (
                        <Container key={index}>
                          <SpaceBetween size="m">
                            <ColumnLayout columns={2}>
                              <FormField 
                                label="Min Bets Count"
                                description={`Required • Min: ${LIMITS.betsCount.min}, Max: ${LIMITS.betsCount.max.toLocaleString()}`}
                                errorText={validationErrors.bet?.[`${index}-minBetsCount`]?.[0]}
                              >
                                <Input
                                  type="number"
                                  value={condition.minBetsCount?.toString() || ''}
                                  onChange={({ detail }) => {
                                    if (detail.value === '') {
                                      updateBetCondition('all', index, 'minBetsCount', undefined);
                                      return;
                                    }

                                    const value = parseInt(detail.value);
                                    if (value > LIMITS.betsCount.max) {
                                      setStore(prev => ({ ...prev, error: `Maximum value is ${LIMITS.betsCount.max.toLocaleString()}` }));
                                      return;
                                    }
                                    
                                    const maxCount = store.formData.triggerConditions.bet.all?.[index]?.maxBetsCount;
                                    if (maxCount !== undefined && value > maxCount) {
                                      setValidationErrors(prev => ({
                                        ...prev,
                                        bet: { 
                                          ...prev.bet, 
                                          [`${index}-minBetsCount`]: ['Minimum count cannot be greater than maximum count'],
                                          [`${index}-maxBetsCount`]: ['Maximum count cannot be less than minimum count']
                                        }
                                      }));
                                    } else {
                                      setValidationErrors(prev => {
                                        const newErrors = { ...prev };
                                        if (newErrors.bet) {
                                          delete newErrors.bet[`${index}-minBetsCount`];
                                          if (maxCount === undefined || value <= maxCount) {
                                            delete newErrors.bet[`${index}-maxBetsCount`];
                                          }
                                        }
                                        return newErrors;
                                      });
                                    }
                                    
                                    updateBetCondition('all', index, 'minBetsCount', value);
                                  }}
                                  invalid={!!validationErrors.bet?.[`${index}-minBetsCount`]}
                                />
                              </FormField>
                              <FormField 
                                label="Max Bets Count"
                                description={`Required • Min: ${LIMITS.betsCount.min}, Max: ${LIMITS.betsCount.max.toLocaleString()}`}
                                errorText={validationErrors.bet?.[`${index}-maxBetsCount`]?.[0]}
                              >
                                <Input
                                  type="number"
                                  value={condition.maxBetsCount?.toString() || ''}
                                  onChange={({ detail }) => {
                                    if (detail.value === '') {
                                      updateBetCondition('all', index, 'maxBetsCount', undefined);
                                      return;
                                    }

                                    const value = parseInt(detail.value);
                                    if (value > LIMITS.betsCount.max) {
                                      setStore(prev => ({ ...prev, error: `Maximum value is ${LIMITS.betsCount.max.toLocaleString()}` }));
                                      return;
                                    }
                                    
                                    const minCount = store.formData.triggerConditions.bet.all?.[index]?.minBetsCount;
                                    if (minCount !== undefined && value < minCount) {
                                      setValidationErrors(prev => ({
                                        ...prev,
                                        bet: { 
                                          ...prev.bet, 
                                          [`${index}-minBetsCount`]: ['Minimum count cannot be greater than maximum count'],
                                          [`${index}-maxBetsCount`]: ['Maximum count cannot be less than minimum count']
                                        }
                                      }));
                                    } else {
                                      setValidationErrors(prev => {
                                        const newErrors = { ...prev };
                                        if (newErrors.bet) {
                                          delete newErrors.bet[`${index}-maxBetsCount`];
                                          if (minCount === undefined || value >= minCount) {
                                            delete newErrors.bet[`${index}-minBetsCount`];
                                          }
                                        }
                                        return newErrors;
                                      });
                                    }
                                    
                                    updateBetCondition('all', index, 'maxBetsCount', value);
                                  }}
                                  invalid={!!validationErrors.bet?.[`${index}-maxBetsCount`]}
                                />
                              </FormField>
                            </ColumnLayout>
                            <Box float="right">
                              <Button 
                                variant="link" 
                                onClick={() => {
                                  const newBet = {...store.formData.triggerConditions.bet};
                                  newBet.all = newBet.all.filter((_, i) => i !== index);
                                  setStore(prev => ({
                                    ...prev,
                                    formData: {
                                      ...prev.formData,
                                      triggerConditions: {
                                        ...prev.formData.triggerConditions,
                                        bet: newBet
                                      }
                                    }
                                  }));
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          </SpaceBetween>
                        </Container>
                      ))}
                    </SpaceBetween>
                  </Container>
                </SpaceBetween>
              )
            },
            {
              label: "Frequency",
              id: "frequency",
              content: (
                <SpaceBetween size="l">
                  <ExpandableSection 
                    headerText="Understanding Frequency Rules"
                    variant="default"
                    headerDescription="Click to see detailed explanation of frequency rules"
                  >
                    <Alert type="info">
                      <SpaceBetween size="xs">
                        <div>Frequency controls how often promotions can trigger. You can configure:</div>
                        <ul>
                          <li><strong>Per User rules only</strong> - each player has their own separate counter (e.g., each player can use 2 times)</li>
                          <li><strong>Global rules only</strong> - one shared counter for all players (e.g., total 10 uses by anyone)</li>
                          <li><strong>Both</strong> - both limits apply (e.g., each player max 2 times AND total max 10 uses)</li>
                        </ul>
                        <div><strong>Rule Types:</strong></div>
                        <ul>
                          <li><strong>Lifetime:</strong> Total limit for the entire duration of the promotion</li>
                          <li><strong>Rolling Window:</strong> Limit within a moving time window (e.g., max 2 times in any 1-hour period)</li>
                          <li><strong>Per Day:</strong> Daily limit that resets at midnight</li>
                        </ul>
                        <div><strong>Counts On:</strong></div>
                        <ul>
                          <li><strong>Use:</strong> Counts only successful deposits - when player actually uses the promotion and gets the bonus</li>
                          <li><strong>Trigger:</strong> Counts every activation - when promotion conditions are met and notification is sent (even if player ignores it)</li>
                        </ul>
                        <div><strong>Example:</strong> Per User: 2 per day (use) + Global: 10 per day (use) = Each player can deposit max 2 times/day, total max 10 deposits/day from all players.</div>
                      </SpaceBetween>
                    </Alert>
                  </ExpandableSection>
                  
                  {validationErrors.frequency && Array.isArray(validationErrors.frequency) && (
                    <Alert type="error">
                      {validationErrors.frequency[0]}
                    </Alert>
                  )}
                  
                  {store.formData.triggerConditions.frequency.length < 2 && (
                    <Container header={<Header variant="h3">Configure Frequency Rules</Header>}>
                      <SpaceBetween direction="horizontal" size="m">
                        {!store.formData.triggerConditions.frequency.find(f => f.scope === 'per_user') && (
                          <Button 
                            onClick={() => addFrequencyScope('per_user')}
                            variant={store.formData.triggerConditions.frequency.length === 0 ? 'primary' : 'normal'}
                          >
                            Add Per User Rules
                          </Button>
                        )}
                        {!store.formData.triggerConditions.frequency.find(f => f.scope === 'global') && (
                          <Button onClick={() => addFrequencyScope('global')}>
                            Add Global Rules
                          </Button>
                        )}
                      </SpaceBetween>
                    </Container>
                  )}
                  
                  {store.formData.triggerConditions.frequency.map((scope, scopeIndex) => (
                    <Container key={scopeIndex} header={
                      <Header 
                        variant="h2"
                        description={scope.scope === 'per_user' 
                          ? 'Each player has their own separate limit counter' 
                          : 'One shared limit counter for all players combined'}
                        actions={
                          <Button 
                            onClick={() => removeFrequencyScope(scopeIndex)}
                            variant="inline-link"
                            iconName="remove"
                          >
                            Remove Scope
                          </Button>
                        }
                      >
                        {scope.scope === 'per_user' ? 'Per User Frequency' : 'Global Frequency'}
                      </Header>
                    }>
                      <SpaceBetween size="m">
                        {scope.rules.map((rule, ruleIndex) => (
                          <Container key={ruleIndex} header={
                            <Header 
                              variant="h3"
                              actions={
                                scope.rules.length > 1 && (
                                  <Button 
                                    onClick={() => removeFrequencyRule(scopeIndex, ruleIndex)}
                                    variant="inline-link"
                                    iconName="remove"
                                  >
                                    Remove
                                  </Button>
                                )
                              }
                            >
                              Rule #{ruleIndex + 1} - {rule.type === 'lifetime' ? 'Lifetime Limit' : rule.type === 'per_day' ? 'Daily Limit' : rule.type === 'rolling_window' ? 'Rolling Window Limit' : 'Frequency Rule'}
                            </Header>
                          }>
                            <ColumnLayout columns={3}>
                              <FormField 
                                label="Rule Type"
                              >
                                <Select
                                  selectedOption={{ 
                                    label: rule.type === 'lifetime' ? 'Lifetime (all time)' : 
                                           rule.type === 'rolling_window' ? 'Rolling Window' : 
                                           rule.type === 'per_day' ? 'Per Day' : rule.type,
                                    value: rule.type 
                                  }}
                                  onChange={({ detail }) => updateFrequencyRule(scopeIndex, ruleIndex, 'type', detail.selectedOption.value)}
                                  options={[
                                    { label: 'Lifetime (all time)', value: 'lifetime' },
                                    { label: 'Rolling Window', value: 'rolling_window' },
                                    { label: 'Per Day', value: 'per_day' }
                                  ]}
                                />
                              </FormField>

                              <FormField 
                                label="Max Triggers"
                                errorText={validationErrors.frequency?.[`${scopeIndex}-${ruleIndex}-maxTriggers`]?.[0]}
                              >
                                <Input
                                  type="text"
                                  value={rule.maxTriggers?.toString() || '0'}
                                  onChange={({ detail }) => {
                                    const cleanValue = detail.value.replace(/\D/g, '');
                                    const value = cleanValue ? parseInt(cleanValue) : 0;
                                    
                                    const errors = validateField('maxTriggers', value, LIMITS.maxTriggers);
                                    if (errors.length > 0) {
                                      setValidationErrors(prev => ({
                                        ...prev,
                                        frequency: { 
                                          ...prev.frequency, 
                                          [`${scopeIndex}-${ruleIndex}-maxTriggers`]: errors 
                                        }
                                      }));
                                    } else {
                                      setValidationErrors(prev => {
                                        const newErrors = { ...prev };
                                        if (newErrors.frequency) {
                                          delete newErrors.frequency[`${scopeIndex}-${ruleIndex}-maxTriggers`];
                                        }
                                        return newErrors;
                                      });
                                    }
                                    updateFrequencyRule(scopeIndex, ruleIndex, 'maxTriggers', value);
                                  }}
                                  placeholder="e.g. 2"
                                  invalid={!!validationErrors.frequency?.[`${scopeIndex}-${ruleIndex}-maxTriggers`]}
                                />
                              </FormField>

                              <FormField 
                                label="Counts On"
                              >
                                <Select
                                  selectedOption={{ 
                                    label: rule.countsOn === 'use' ? 'Use (deposits made)' : 'Trigger (promotions shown)', 
                                    value: rule.countsOn 
                                  }}
                                  onChange={({ detail }) => updateFrequencyRule(scopeIndex, ruleIndex, 'countsOn', detail.selectedOption.value)}
                                  options={[
                                    { label: 'Use (deposits made)', value: 'use' },
                                    { label: 'Trigger (promotions shown)', value: 'trigger' }
                                  ]}
                                />
                              </FormField>

                              {rule.type === 'rolling_window' && (
                                <FormField 
                                  label="Time Window (seconds)"
                                  description={(() => {
                                    const seconds = rule.windowSeconds || 0;
                                    if (seconds === 0) return "Not set";
                                    if (seconds < 60) return `${seconds}`;
                                    if (seconds < 3600) return `${(seconds / 60).toFixed(1)} minutes`;
                                    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} hours`;
                                    return `${(seconds / 86400).toFixed(1)} days`;
                                  })()}
                                  errorText={validationErrors.frequency?.[`${scopeIndex}-${ruleIndex}-windowSeconds`]?.[0]}
                                >
                                  <Input
                                    type="text"
                                    value={rule.windowSeconds?.toString() || ''}
                                    onChange={({ detail }) => {
                                      const cleanValue = detail.value.replace(/\D/g, '');
                                      if (cleanValue === '') {
                                        updateFrequencyRule(scopeIndex, ruleIndex, 'windowSeconds', 0);
                                        return;
                                      }
                                      
                                      const value = parseInt(cleanValue) || 0;
                                      const errors = validateField('windowSeconds', value, LIMITS.windowSeconds);
                                      if (errors.length > 0) {
                                        setValidationErrors(prev => ({
                                          ...prev,
                                          frequency: { 
                                            ...prev.frequency, 
                                            [`${scopeIndex}-${ruleIndex}-windowSeconds`]: errors 
                                          }
                                        }));
                                      } else {
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          if (newErrors.frequency) {
                                            delete newErrors.frequency[`${scopeIndex}-${ruleIndex}-windowSeconds`];
                                          }
                                          return newErrors;
                                        });
                                      }
                                      updateFrequencyRule(scopeIndex, ruleIndex, 'windowSeconds', value);
                                    }}
                                    placeholder="e.g. 3600"
                                    invalid={!!validationErrors.frequency?.[`${scopeIndex}-${ruleIndex}-windowSeconds`]}
                                  />
                                </FormField>
                              )}
                            </ColumnLayout>
                          </Container>
                        ))}
                        <Button 
                          onClick={() => addFrequencyRule(scopeIndex)}
                          iconName="add-plus"
                        >
                          Add Rule
                        </Button>
                      </SpaceBetween>
                    </Container>
                  ))}
                </SpaceBetween>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  );
}

export default PersonalPromotionsFormModal;
